<template>
    <div class="cabinet-map-page">
        <div ref="map" class="cabinet-map" :aria-busy="isMapLoading ? 'true' : 'false'" @click="handleMapBackgroundClick"></div>

        <transition name="map-loading-fade">
            <div
                v-if="isMapLoadingVisible"
                class="map-loading-overlay"
                role="status"
                aria-live="polite"
            >
                <div class="map-loading-card">
                    <span class="map-loading-spinner" aria-hidden="true"></span>
                    <span class="map-loading-text">{{ mapLoadingText }}</span>
                </div>
            </div>
        </transition>

        <div v-if="isMapUnavailable" class="map-status">
            {{ mapStatus }}
        </div>

        <transition name="map-menu-fade">
            <div
                v-show="isMapMenuVisible"
                class="map-side-menu-mask"
                aria-hidden="true"
                @click="closeMapMenu"
            ></div>
        </transition>

        <transition name="map-side-menu-slide">
            <aside
                v-show="isMapMenuVisible"
                class="map-side-menu"
                :aria-hidden="isMapMenuVisible ? 'false' : 'true'"
                :aria-modal="isMapMenuVisible ? 'true' : 'false'"
                role="dialog"
            >
                <div class="map-side-menu-brand">
                    <template v-if="brand_logo">
                        <img
                            class="brand-logo"
                            :class="{ loaded: isBrandLogoLoaded }"
                            :src="brand_logo"
                            alt=""
                            decoding="async"
                            @load="handleBrandLogoLoad"
                            @error="handleBrandLogoError"
                        >
                        <template v-if="!isBrandLogoLoaded">
                            <img v-if="brand_favicon" class="brand-icon" :src="brand_favicon" alt="">
                            <span v-else>{{ brand_name }}</span>
                        </template>
                    </template>
                    <template v-else>
                        <img v-if="brand_favicon" class="brand-icon" :src="brand_favicon" alt="">
                        <span>{{ brand_name }}</span>
                    </template>
                </div>
                <div v-if="isLoggedIn && mapUserDisplayText" class="map-side-menu-user">
                    {{ mapUserDisplayText }}
                </div>
                <div class="map-side-menu-list">
                    <button
                        v-if="isLoggedIn"
                        v-active-touch
                        class="map-side-menu-item interactive-surface"
                        type="button"
                        @click="openProfilePage"
                    >
                        <img class="map-side-menu-icon" :src="mapIconUrls.menuProfile" alt="">
                        <span>{{ $t("m.mapMenuYourInfo") }}</span>
                    </button>
                    <button
                        v-if="showMapLanguageEntry"
                        v-active-touch
                        class="map-side-menu-item interactive-surface"
                        type="button"
                        @click="openMapLanguagePopup"
                    >
                        <img class="map-side-menu-icon" :src="mapIconUrls.menuLanguage" alt="">
                        <span>{{ $t("m.mapMenuLanguage") }}</span>
                    </button>
                    <button
                        v-active-touch
                        class="map-side-menu-item interactive-surface"
                        type="button"
                        @click="openHelpPage"
                    >
                        <MenuHelpIcon class="map-side-menu-icon" aria-hidden="true" />
                        <span>{{ $t("m.mapMenuHelp") }}</span>
                    </button>
                    <button v-active-touch class="map-side-menu-item interactive-surface" type="button" @click="handleMapMenuAuth">
                        <img class="map-side-menu-icon" :src="isLoggedIn ? mapIconUrls.menuLogout : mapIconUrls.menuLogin" alt="">
                        <span>{{ isLoggedIn ? $t("m.mapMenuLogout") : $t("m.mapMenuLogin") }}</span>
                    </button>
                </div>
            </aside>
        </transition>

        <button v-if="!isStationListVisible" v-active-touch class="map-btn map-menu-btn interactive-icon" type="button" @click="openMapMenu" :aria-label="$t('m.mapMenu')">
            <img class="map-icon-img" :src="mapIconUrls.menu" alt="">
        </button>

        <button
            v-if="!isStationListVisible"
            v-active-touch
            class="map-btn map-search-btn interactive-icon"
            type="button"
            :aria-label="$t('m.nearbyStationSearchAria')"
            @click="openStationList('rent')"
        >
            <img class="map-icon-img" :src="mapIconUrls.search" alt="">
        </button>

        <div
            v-if="contextIslandVisible && !isStationListVisible"
            class="map-context-island"
            :class="{ expanded: contextIslandExpanded, actionable: contextIslandCanNavigate }"
            @click.stop="handleContextIslandClick"
        >
            <div class="map-context-main">
                <span
                    v-if="!contextIslandExpanded"
                    class="map-context-collapsed-dot"
                    :class="contextStatusClass"
                    aria-hidden="true"
                ></span>
                <span class="map-context-title">{{ contextIslandTitle }}</span>
            </div>
            <span
                class="map-context-toggle-arrow"
                :class="{ expanded: contextIslandExpanded }"
                aria-hidden="true"
            ></span>
            <span v-if="contextIslandExpanded && contextIslandCanNavigate" class="map-context-arrow" aria-hidden="true"></span>
            <div v-if="contextIslandExpanded" class="map-context-detail" :class="{ renting: isRentingContext }">
                <template v-if="isRentingContext">
                    <div class="map-context-rental-row">
                        <div class="map-context-rental-metric">
                            <span class="map-context-rental-label">Duration</span>
                            <span class="map-context-rental-value">{{ contextRentalDuration }}</span>
                        </div>
                        <div class="map-context-rental-metric">
                            <span class="map-context-rental-label">Estimated</span>
                            <span class="map-context-rental-value">{{ contextRentalEstimatedFee }}</span>
                        </div>
                    </div>
                    <div class="map-context-rental-action">{{ $t("m.tapToReturnRentalPage") }}</div>
                </template>
                <template v-else>
                    <span class="map-context-status">
                        <span class="map-context-status-dot" :class="contextStatusClass" aria-hidden="true"></span>
                        <span>{{ contextIslandSubtitle }}</span>
                    </span>
                    <span class="map-context-action">{{ contextIslandActionText }}</span>
                </template>
            </div>
        </div>

        <button
            v-if="!isStationListVisible && (showScanButton || showReturnButton)"
            v-active-touch
            class="scan-btn interactive-press"
            :class="{ renting: isRentingContext }"
            type="button"
            @click="isRentingContext ? openStationList('return') : openScanner()"
        >
            <ScanIcon v-if="showBottomActionButtonIcon" class="scan-icon map-icon-img" aria-hidden="true" />
            <span>{{ bottomActionButtonText }}</span>
        </button>

        <button
            v-if="!isStationListVisible"
            v-active-touch
            class="map-btn map-location-btn interactive-icon"
            type="button"
            :aria-label="$t('m.currentLocation')"
            @click="focusCurrentLocation"
        >
            <img class="map-icon-img" :src="mapIconUrls.locationButton" alt="">
        </button>

        <transition name="fade">
            <div
                v-if="!isStationListVisible && selectedCabinet && isDetailSheetExpanded"
                class="cabinet-detail-overlay"
                aria-hidden="true"
            ></div>
        </transition>

        <div
            v-if="selectedCabinet && (!isStationListVisible || isStationDetailVisible)"
            class="cabinet-detail-sheet"
            :class="{
                expanded: isDetailSheetExpanded,
                dragging: isDetailSheetDragging,
                closing: isDetailSheetClosing,
                'station-detail-fullscreen': isStationDetailVisible
            }"
            @mousedown="startDetailSheetGesture"
            @touchstart="startDetailSheetGesture"
        >
            <div class="cabinet-detail-header">
                <button
                    v-if="isStationDetailVisible"
                    v-active-touch
                    class="cabinet-detail-back interactive-icon"
                    type="button"
                    :aria-label="$t('m.backToMap')"
                    @click.stop="backToStationList"
                ></button>
                <div class="cabinet-detail-handle"></div>
                <h2 class="cabinet-detail-title">{{ selectedCabinetTitle }}</h2>
                <div class="cabinet-detail-address">{{ selectedCabinetAddress }}</div>
                <div class="cabinet-detail-status">
                    <span :class="{ open: isSelectedCabinetOpen, closed: !isSelectedCabinetOpen }">
                        {{ selectedCabinetStatusText }}
                    </span>
                    <span class="cabinet-detail-dot">•</span>
                    <span>{{ selectedCabinetHoursText }}</span>
                </div>
            </div>

            <div
                ref="cabinetDetailContent"
                class="cabinet-detail-content"
                :class="{ 'no-gallery': !selectedCabinetImages.length }"
            >
                <div class="cabinet-detail-metrics">
                    <div>
                        <span>{{ $t("m.nearbyStationAvailable") }}</span>
                        <strong :class="{ empty: selectedCabinetAvailable === 0 }">{{ selectedCabinetAvailable }}</strong>
                    </div>
                    <div>
                        <span>{{ $t("m.nearbyStationEmpty") }}</span>
                        <strong :class="{ empty: selectedCabinetEmpty === 0 }">{{ selectedCabinetEmpty }}</strong>
                    </div>
                    <div>
                        <span>{{ $t("m.nearbyStationDistance") }}</span>
                        <strong>{{ selectedCabinetDistance }}</strong>
                    </div>
                </div>

                <div v-if="selectedCabinetImages.length" class="cabinet-detail-gallery">
                    <img
                        v-for="image in selectedCabinetImages"
                        :key="image"
                        :src="image"
                        alt=""
                    >
                </div>

                <div v-if="selectedCabinet.website" class="cabinet-detail-row">
                    <img class="cabinet-detail-row-icon" :src="mapIconUrls.detailWebsite" alt="">
                    <span>{{ selectedCabinet.website }}</span>
                </div>
                <div
                    v-if="hasSelectedCabinetWorkTimes"
                    class="cabinet-detail-worktime"
                    :class="{ open: isWorkTimesExpanded }"
                >
                    <button v-active-touch class="cabinet-detail-row cabinet-detail-worktime-toggle interactive-surface" type="button" @click.stop="toggleWorkTimes">
                        <img class="cabinet-detail-row-icon" :src="mapIconUrls.detailClock" alt="">
                        <span>{{ selectedCabinetTodayHoursText }}</span>
                        <span class="cabinet-detail-row-chevron" aria-hidden="true"></span>
                    </button>
                    <div v-if="isWorkTimesExpanded" class="cabinet-detail-worktime-list">
                        <div
                            v-for="item in selectedCabinetWorkTimes"
                            :key="item.day"
                            class="cabinet-detail-worktime-item"
                            :class="{ today: item.isToday }"
                        >
                            <span>{{ item.day }}</span>
                            <strong>{{ item.time }}</strong>
                        </div>
                    </div>
                </div>
            </div>

            <div class="cabinet-detail-actions">
                <button v-active-touch class="cabinet-action-primary interactive-press" type="button" @click.stop="openDirections">
                    <img class="cabinet-action-icon" :src="mapIconUrls.detailDirections" alt="">
                    <span>{{ $t("m.directions") }}</span>
                </button>
                <button
                    v-if="selectedCabinet.phone"
                    v-active-touch
                    class="cabinet-action-secondary interactive-press"
                    type="button"
                    @click.stop="callSelectedCabinet"
                >
                    <img class="cabinet-action-icon" :src="mapIconUrls.detailCall" alt="">
                    <span>{{ $t("m.call") }}</span>
                </button>
            </div>
        </div>

        <div v-if="isStationListVisible" class="station-list-page">
            <div class="station-list-header">
                <button v-active-touch class="station-list-back interactive-icon" type="button" :aria-label="$t('m.backToMap')" @click="closeStationList"></button>
                <h1>{{ $t("m.nearbyStationTitle") }}</h1>
                <label class="station-search-field">
                    <span class="station-search-icon" aria-hidden="true"></span>
                    <input
                        v-model.trim="stationSearchKeyword"
                        type="search"
                        autocomplete="off"
                        :placeholder="$t('m.nearbyStationSearchPlaceholder')"
                    >
                </label>
                <div class="station-tabs" role="tablist" :aria-label="$t('m.nearbyStationType')">
                    <button
                        v-active-touch
                        class="interactive-text"
                        type="button"
                        role="tab"
                        :aria-selected="stationListMode === 'rent'"
                        :class="{ active: stationListMode === 'rent' }"
                        @click="stationListMode = 'rent'"
                    >
                        {{ $t("m.nearbyStationRent") }}
                    </button>
                    <button
                        v-active-touch
                        class="interactive-text"
                        type="button"
                        role="tab"
                        :aria-selected="stationListMode === 'return'"
                        :class="{ active: stationListMode === 'return' }"
                        @click="stationListMode = 'return'"
                    >
                        {{ $t("m.nearbyStationReturn") }}
                    </button>
                </div>
            </div>

            <div class="station-list-content">
                <template v-if="stationListLoading">
                    <div
                        v-for="item in 5"
                        :key="`station-skeleton-${item}`"
                        class="station-card station-card-skeleton"
                        aria-hidden="true"
                    >
                        <span class="station-card-skeleton-image"></span>
                        <span class="station-card-skeleton-main">
                            <i class="station-card-skeleton-line title"></i>
                            <i class="station-card-skeleton-line address"></i>
                            <i class="station-card-skeleton-line hours"></i>
                        </span>
                        <span class="station-card-divider"></span>
                        <span class="station-card-skeleton-metrics">
                            <i></i>
                            <i></i>
                            <i></i>
                        </span>
                    </div>
                </template>
                <template v-else>
                    <button
                        v-for="card in nearbyStationCards"
                        :key="card.key"
                        v-active-touch
                        class="station-card interactive-surface"
                        type="button"
                        @click="selectStationFromList(card.cabinet)"
                    >
                        <span class="station-card-image-wrap" :class="{ empty: !card.image }">
                            <img v-if="card.image" :src="card.image" alt="">
                        </span>
                        <span class="station-card-main">
                            <strong>{{ card.title }}</strong>
                            <span class="station-card-address">{{ card.address }}</span>
                            <span class="station-card-hours">
                                <span :class="card.isOpen ? 'open' : 'closed'">{{ card.statusText }}</span>
                                <span class="station-card-dot">•</span>
                                <span>{{ card.hoursText }}</span>
                            </span>
                        </span>
                        <span class="station-card-divider" aria-hidden="true"></span>
                        <span class="station-card-metrics">
                            <span>
                                <small>{{ $t("m.nearbyStationAvailable") }}</small>
                                <b>{{ card.available }}</b>
                            </span>
                            <span>
                                <small>{{ $t("m.nearbyStationEmpty") }}</small>
                                <b :class="{ muted: card.empty === 0 }">{{ card.empty }}</b>
                        </span>
                        <span class="station-card-distance">
                            <small>{{ card.distance }}</small>
                            <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="10" fill="#F3F3F3"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.44423 10.3463C6.99426 10.3463 6.62949 10.6937 6.62949 11.1222V13.2236C6.62949 13.6522 6.26471 13.9996 5.81474 13.9996C5.36477 13.9996 5 13.6522 5 13.2236V11.1222C5 9.83658 6.09432 8.79437 7.44423 8.79437H12.6056V10.3463H7.44423Z" fill="#333333"/>
                                <path d="M14.8366 9.08625C15.1132 9.28582 15.1132 9.69757 14.8366 9.89714L12.0009 11.9436C11.6703 12.1823 11.2083 11.946 11.2083 11.5382V7.4452C11.2083 7.0374 11.6703 6.80111 12.0009 7.03976L14.8366 9.08625Z" fill="#333333"/>
                            </svg>
                        </span>
                    </span>
                </button>
                </template>

                <div v-if="!stationListLoading && !nearbyStationCards.length" class="station-list-empty">
                    {{ stationListEmptyText }}
                </div>
            </div>
        </div>

        <ScannerOverlay
            v-if="isScannerOpen"
            :scan-parser="parseDeviceCodeFromScanResult"
            @close="closeScanner"
            @scan="handleScanResult"
        />

        <Popup :title="$t('m.langBoxTitle')" :showPopup="isLanguagePopupVisible" @hidePopup="isLanguagePopupVisible = false">
            <div class="map-language-options">
                <button
                    v-for="(lang, key) in langList"
                    :key="key"
                    v-active-touch
                    class="map-language-option interactive-surface"
                    :class="{ selected: key === $i18n.locale }"
                    type="button"
                    @click="changeMapLanguage(key)"
                >
                    {{ lang }}
                </button>
            </div>
        </Popup>

        <PopupConfirm
            class="map-logout-popup"
            :title="$t('m.mapMenuLogoutConfirmTitle')"
            :showPopup="isLogoutConfirmVisible"
            :body-scrollable="false"
            @hidePopup="isLogoutConfirmVisible = false"
        >
            <div class="map-logout-confirm">
                <div class="map-logout-content">{{ $t("m.mapMenuLogoutConfirmContent") }}</div>
                <button v-active-touch class="map-logout-confirm-btn primary-btn" type="button" @click="confirmLogout">
                    {{ $t("m.mapMenuLogout") }}
                </button>
            </div>
        </PopupConfirm>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import Popup from "@/components/popups/Popup.vue";
import PopupConfirm from "@/components/popups/PopupConfirm.vue";
import ScannerOverlay from "@/components/ScannerOverlay.vue";
import MenuHelpIcon from "@/components/icons/MenuHelpIcon.vue";
import ScanIcon from "@/components/icons/ScanIcon.vue";
import { langOptions } from "@/common/lang";
import { cks } from "@/utils/tool";
import { getNearCabinetList, getUserInfo, queryBorrowStatus } from "@/api/logined";
import { setPostLoginRedirect } from "@/utils/postLoginRedirect";
import menuIcon from "@/assets/map/menu.svg";
import searchIcon from "@/assets/map/search.svg";
import locationButtonIcon from "@/assets/map/location-button.svg";
import menuLanguageIcon from "@/assets/map/menu-language.svg";
import menuLoginIcon from "@/assets/map/menu-login.svg";
import menuLogoutIcon from "@/assets/map/menu-logout.svg";
import menuProfileIcon from "@/assets/map/menu-profile.svg";
import detailClockIcon from "@/assets/map/detail-clock.svg";
import detailWebsiteIcon from "@/assets/map/detail-website.svg";
import detailDirectionsIcon from "@/assets/map/detail-directions.svg";
import detailCallIcon from "@/assets/map/detail-call.svg";
import simulatedCabinetOnlineIcon from "@/assets/map/mark.png";

const GOOGLE_MAPS_CALLBACK = "__heyChargeGoogleMapsReady";
const GOOGLE_MAPS_KEY = process.env.VUE_APP_GOOGLE_MAPS_KEY || "AIzaSyBzT_BR3ncEkaB9gLMoMpH-s4ToFYsqcko";
const GOOGLE_MAPS_LOAD_TIMEOUT = 3000;
const DEFAULT_CENTER = {
    lat: 9.049859,
    lng: -79.462032,
};
const CABINET_MARKER_SIZE = {
    width: 28.938,
    height: 42,
};
const CUSTOM_CABINET_MARKER_SIZE = {
    width: 12,
    height: 30,
};
const CABINET_MARKER_HIT_PADDING = 10;
const CURRENT_LOCATION_MARKER_SIZE = {
    width: 72,
    height: 72,
    anchorX: 36,
    anchorY: 58,
};
const OWN_LOCATION_MARKER_SIZE = {
    width: 34,
    height: 34,
};
const MAP_CABINET_REQUEST_DEBOUNCE = 600;
const SECONDS_IN_DAY = 24 * 60 * 60;
const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
const WEEK_DAYS = [
    { longKey: "m.weekdayMonday", shortKey: "m.weekdayMon" },
    { longKey: "m.weekdayTuesday", shortKey: "m.weekdayTue" },
    { longKey: "m.weekdayWednesday", shortKey: "m.weekdayWed" },
    { longKey: "m.weekdayThursday", shortKey: "m.weekdayThu" },
    { longKey: "m.weekdayFriday", shortKey: "m.weekdayFri" },
    { longKey: "m.weekdaySaturday", shortKey: "m.weekdaySat" },
    { longKey: "m.weekdaySunday", shortKey: "m.weekdaySun" },
];

const MAP_ICON_URLS = {
    menu: menuIcon,
    search: searchIcon,
    locationButton: locationButtonIcon,
    menuLanguage: menuLanguageIcon,
    menuLogin: menuLoginIcon,
    menuLogout: menuLogoutIcon,
    menuProfile: menuProfileIcon,
    detailClock: detailClockIcon,
    detailWebsite: detailWebsiteIcon,
    detailDirections: detailDirectionsIcon,
    detailCall: detailCallIcon,
    cabinetOnlineFallback: simulatedCabinetOnlineIcon,
};

const CABINET_MAP_STATION_LIST_MODE_KEY = 'cabinetMapStationListModeOnce';

const markerIconCache = {};

function normalizeAssetUrl(url) {
    if (!url) return "";
    if (/^(data:|https?:|blob:)/i.test(url)) return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return url;
}

function loadMarkerImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

function drawImageContained(ctx, image, x, y, width, height) {
    const imageRatio = image.width / image.height;
    const boxRatio = width / height;
    const drawWidth = imageRatio > boxRatio ? width : height * imageRatio;
    const drawHeight = imageRatio > boxRatio ? width / imageRatio : height;
    ctx.drawImage(
        image,
        x + ((width - drawWidth) / 2),
        y + ((height - drawHeight) / 2),
        drawWidth,
        drawHeight
    );
}

async function createMarkerIcon(imageUrl, count = 1, isMuted = false, isCustomIcon = false) {
    const normalizedUrl = normalizeAssetUrl(imageUrl);
    const pixelRatio = Math.max(2, Math.min(window.devicePixelRatio || 2, 4));
    const markerSize = isCustomIcon ? CUSTOM_CABINET_MARKER_SIZE : CABINET_MARKER_SIZE;
    const displayWidth = markerSize.width + (CABINET_MARKER_HIT_PADDING * 2);
    const displayHeight = markerSize.height + (CABINET_MARKER_HIT_PADDING * 2);
    const cacheKey = `${normalizedUrl}|${count}|${isMuted ? "muted" : "color"}|${pixelRatio}|hit-${CABINET_MARKER_HIT_PADDING}|custom-${isCustomIcon}`;
    if (markerIconCache[cacheKey]) return markerIconCache[cacheKey];

    let url = normalizedUrl;
    try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(displayWidth * pixelRatio);
        canvas.height = Math.ceil(displayHeight * pixelRatio);
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        const ctx = canvas.getContext("2d");
        const image = await loadMarkerImage(normalizedUrl);
        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.globalAlpha = isMuted ? 0.6 : 1;
        if (isCustomIcon) {
            drawImageContained(
                ctx,
                image,
                CABINET_MARKER_HIT_PADDING,
                CABINET_MARKER_HIT_PADDING,
                markerSize.width,
                markerSize.height
            );
        } else {
            ctx.drawImage(
                image,
                CABINET_MARKER_HIT_PADDING,
                CABINET_MARKER_HIT_PADDING,
                markerSize.width,
                markerSize.height
            );
        }
        ctx.globalAlpha = 1;

        if (count > 1) {
            const badgeText = count > 99 ? "99+" : String(count);
            const badgeX = CABINET_MARKER_HIT_PADDING + markerSize.width - 6.5;
            const badgeY = CABINET_MARKER_HIT_PADDING + 8;
            ctx.beginPath();
            ctx.arc(badgeX, badgeY, 7, 0, Math.PI * 2);
            ctx.fillStyle = "#FF3B30";
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#fff";
            ctx.stroke();
            ctx.fillStyle = "#fff";
            ctx.font = "700 9px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(badgeText, badgeX, badgeY + 0.5);
        }

        url = canvas.toDataURL("image/png");
    } catch (_) {
        url = normalizedUrl;
    }

    markerIconCache[cacheKey] = {
        url,
        scaledSize: new window.google.maps.Size(displayWidth, displayHeight),
        anchor: new window.google.maps.Point(
            CABINET_MARKER_HIT_PADDING + (markerSize.width / 2),
            CABINET_MARKER_HIT_PADDING + markerSize.height
        ),
        shapeCoords: [0, 0, displayWidth, displayHeight],
    };
    return markerIconCache[cacheKey];
}

function createCurrentLocationIcon(offsetY = 0, landingCircleProgress = 0) {
    const circleProgress = Math.max(0, Math.min(Number(landingCircleProgress) || 0, 1));
    const landingCircle = circleProgress > 0
        ? `<circle cx="36" cy="42" r="${8 + (circleProgress * 28)}" fill="#2F80ED" fill-opacity="${0.18 * (1 - circleProgress)}"/>`
        : "";
    const svg = `
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            ${landingCircle}
            <circle cx="36" cy="20" r="10" fill="#056BF5"/>
            <circle cx="36" cy="20" r="3" fill="white"/>
            <rect x="35" y="30" width="2" height="17" fill="#056BF5"/>
            <ellipse cx="36" cy="48" rx="4" ry="1.5" fill="#B7BEC8" fill-opacity="0.7"/>
        </svg>
    `;

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(CURRENT_LOCATION_MARKER_SIZE.width, CURRENT_LOCATION_MARKER_SIZE.height),
        anchor: new window.google.maps.Point(
            CURRENT_LOCATION_MARKER_SIZE.anchorX,
            CURRENT_LOCATION_MARKER_SIZE.anchorY + offsetY
        ),
    };
}

function createOwnLocationIcon(heading) {
    const normalizedHeading = Number.isFinite(Number(heading)) ? Number(heading) : 0;
    const svg = `
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g transform="rotate(${normalizedHeading} 17 17)">
                <path d="M17 1.6L24 18.4L17 15.7L10 18.4L17 1.6Z" fill="#2F80ED" stroke="white" stroke-width="2" stroke-linejoin="round"/>
                <circle cx="17" cy="20" r="8.6" fill="#2F80ED" stroke="white" stroke-width="2.6"/>
            </g>
        </svg>
    `;

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(OWN_LOCATION_MARKER_SIZE.width, OWN_LOCATION_MARKER_SIZE.height),
        anchor: new window.google.maps.Point(
            OWN_LOCATION_MARKER_SIZE.width / 2,
            OWN_LOCATION_MARKER_SIZE.height / 2
        ),
    };
}

export default {
    name: "CabinetMap",
    components: {
        Popup,
        PopupConfirm,
        ScannerOverlay,
        MenuHelpIcon,
        ScanIcon,
    },
    data() {
        return {
            map: null,
            userMarker: null,
            ownLocationMarker: null,
            cabinetMarkers: [],
            selectedCabinetGroup: null,
            isDetailSheetExpanded: false,
            isDetailSheetDragging: false,
            detailSheetDragStartY: 0,
            detailSheetGestureMode: "",
            detailSheetContentStartScrollTop: 0,
            detailSheetCloseTimer: null,
            isDetailSheetClosing: false,
            isWorkTimesExpanded: false,
            suppressMapClickUntil: 0,
            markerInteractionSuppressRefreshUntil: 0,
            currentPosition: DEFAULT_CENTER,
            isMapUnavailable: false,
            mapStatus: "",
            mapIdleListener: null,
            mapClickListener: null,
            cabinetRequestTimer: null,
            cabinetRequestSeq: 0,
            skipNextMapIdleRefresh: false,
            currentLocationAnimationTimer: null,
            currentLocationAnimationFrame: null,
            currentLocationLandingFrame: null,
            currentLocationPointerOffset: 0,
            isMapLoading: false,
            isMapLoadingVisible: false,
            mapLoadingVisibleTimer: null,
            mapLoadingMaxTimer: null,
            mapLoadingReason: "initial",
            hasInitialMapLoadCompleted: false,
            googleMapsReloadPromise: null,
            isScannerOpen: false,
            isHandlingScanResult: false,
            cabinetList: [],
            stationList: [],
            stationListRequestSeq: 0,
            stationListLoading: false,
            isStationListVisible: false,
            isStationDetailVisible: false,
            stationSearchKeyword: "",
            stationListMode: "rent",
            contextIslandExpanded: false,
            contextRentDetail: {},
            contextRentalDuration: "--",
            contextRentalTimer: null,
            isBorrowStatusResolved: false,
            isBrandLogoLoaded: false,
            isMapMenuVisible: false,
            isLanguagePopupVisible: false,
            isLogoutConfirmVisible: false,
            mapUserInfo: {},
            isMapUserInfoLoading: false,
            langList: {},
        };
    },
    computed: {
        ...mapGetters("appConfig", ["brand_name", "brand_logo", "brand_favicon", "device_code", "machine_precheck_error", "web_lang_options"]),
        ...mapGetters(["token", "is_borrow", "order_info"]),
        mapIconUrls() {
            return MAP_ICON_URLS;
        },
        showMapLanguageEntry() {
            return Array.isArray(this.web_lang_options) && this.web_lang_options.length > 0;
        },
        isLoggedIn() {
            return !!this.token;
        },
        mapUserDisplayText() {
            if (this.isMapUserInfoLoading && !Object.keys(this.mapUserInfo || {}).length) {
                return this.$t("m.mapMenuLoadingProfile");
            }

            const user = this.mapUserInfo || {};
            const fullName = [
                user.first_name || user.firstName,
                user.last_name || user.lastName,
            ].filter(Boolean).join(" ").trim();

            return fullName ||
                user.name ||
                user.nickname ||
                user.email ||
                user.phone ||
                user.tel ||
                user.mobile ||
                (user.id || user.user_id || user.member_id
                    ? `User ID:${user.id || user.user_id || user.member_id}`
                    : "");
        },
        contextScannedCode() {
            return (this.machine_precheck_error && this.machine_precheck_error.device_code) ||
                this.device_code ||
                "";
        },
        isMachineUnavailableContext() {
            return !!this.machine_precheck_error;
        },
        isBorrowStatusReady() {
            return !this.isLoggedIn || this.isBorrowStatusResolved;
        },
        isMachineAvailableContext() {
            return this.isBorrowStatusReady && !this.isRentingContext && !this.isMachineUnavailableContext && !!this.device_code;
        },
        isRentingContext() {
            return this.isBorrowStatusReady && Number(this.is_borrow) === 1;
        },
        contextIslandVisible() {
            return this.isBorrowStatusReady && (this.isMachineUnavailableContext || this.isMachineAvailableContext || this.isRentingContext);
        },
        contextIslandCanNavigate() {
            return this.isMachineAvailableContext || this.isRentingContext;
        },
        mapLoadingText() {
            return this.$t("m.mapLoading");
        },
        showScanButton() {
            return this.isBorrowStatusReady && !this.isRentingContext;
        },
        showReturnButton() {
            return this.isBorrowStatusReady && this.isRentingContext;
        },
        bottomActionButtonText() {
            return this.isRentingContext
                ? this.$t("m.findNearbyReturnableMachines")
                : (this.isMachineAvailableContext
                    ? this.$t("m.scanOtherMachine")
                    : this.$t("m.scanForPowerbank"));
        },
        showBottomActionButtonIcon() {
            return !this.isRentingContext;
        },
        contextIslandDisplayCode() {
            return this.shortenMiddleText(this.contextScannedCode || "--", 10, 4);
        },
        contextIslandTitle() {
            if (this.isRentingContext) {
                return this.$t("m.rentingMapIslandTitle");
            }
            return this.formatMessage(this.$t("m.scannedMachineWithCode"), {
                code: this.contextIslandDisplayCode,
            });
        },
        contextIslandSubtitle() {
            if (this.isRentingContext) {
                return `${this.$t("m.rentalDuration")} ${this.contextRentalDuration}`;
            }
            if (this.isMachineUnavailableContext) {
                return this.$t("m.mapIslandMachineUnavailableShort");
            }
            return this.$t("m.currentMachineAvailableHint");
        },
        contextIslandActionText() {
            if (this.isRentingContext) {
                return this.$t("m.mapIslandReturnActionShort");
            }
            if (this.isMachineUnavailableContext) {
                return this.$t("m.mapIslandMachineUnavailableActionShort");
            }
            return this.$t("m.mapIslandMachineAvailableActionShort");
        },
        contextRentalEstimatedFee() {
            return this.currencyFormat(this.contextRentDetail.actual_fee, this.contextRentDetail.currency);
        },
        contextStatusClass() {
            if (this.isMachineUnavailableContext) return "unavailable";
            if (this.isRentingContext) return "renting";
            return "available";
        },
        selectedCabinet() {
            const group = this.selectedCabinetGroup;
            return group && group.primaryCabinet
                ? group.primaryCabinet
                : null;
        },
        selectedCabinetTitle() {
            const cabinet = this.selectedCabinet || {};
            return cabinet.outlets_name || cabinet.box_number || this.$t("m.cabinet");
        },
        selectedCabinetAddress() {
            const cabinet = this.selectedCabinet || {};
            return cabinet.address1 || cabinet.address || cabinet.address2 || "";
        },
        isSelectedCabinetOnline() {
            return Number((this.selectedCabinet || {}).online_status) === 1;
        },
        isSelectedCabinetOpen() {
            const schedule = this.selectedCabinetSchedule;
            return schedule ? schedule.isOpen : this.isSelectedCabinetOnline;
        },
        selectedCabinetStatusText() {
            return this.isSelectedCabinetOpen ? this.$t("m.open") : this.$t("m.closed");
        },
        selectedCabinetHoursText() {
            const schedule = this.selectedCabinetSchedule;
            if (schedule) {
                return `${schedule.nextAction} ${schedule.nextTime} ${schedule.nextDay}`;
            }

            const cabinet = this.selectedCabinet || {};
            if (cabinet.start && cabinet.end) {
                return this.isSelectedCabinetOpen
                    ? `${this.$t("m.closes")} ${cabinet.end}`
                    : `${this.$t("m.opens")} ${cabinet.start}`;
            }
            return this.$t("m.hoursUnavailable");
        },
        selectedCabinetSchedule() {
            return this.getCabinetSchedule(this.selectedCabinet || {});
        },
        selectedCabinetAvailable() {
            return Number((this.selectedCabinet || {}).borrow) || 0;
        },
        selectedCabinetEmpty() {
            return Number((this.selectedCabinet || {}).back) || 0;
        },
        selectedCabinetDistance() {
            const position = this.getCabinetPosition(this.selectedCabinet || {});
            const distanceMeters = this.getDistanceMeters(this.currentPosition, position);
            return this.formatCabinetDistance(distanceMeters);
        },
        selectedCabinetImages() {
            return this.getCabinetImages(this.selectedCabinet || {});
        },
        hasSelectedCabinetWorkTimes() {
            return this.getCabinetWorkTimes(this.selectedCabinet || {}).length > 0;
        },
        selectedCabinetWorkTimes() {
            return this.getCabinetWorkTimes(this.selectedCabinet || {});
        },
        selectedCabinetTodayHoursText() {
            const todayWorkTime = this.selectedCabinetWorkTimes.find((item) => item.isToday);
            return todayWorkTime ? todayWorkTime.time : this.$t("m.closed");
        },
        nearbyStationCards() {
            const keyword = this.stationSearchKeyword.toLowerCase();
            return this.stationList
                .filter((cabinet) => (
                    this.stationListMode === "return"
                        ? Number(cabinet.back) > 0
                        : Number(cabinet.borrow) > 0
                ))
                .map((cabinet, index) => this.getStationCard(cabinet, index))
                .filter((card) => {
                    if (!keyword) return true;
                    return `${card.title} ${card.address}`.toLowerCase().includes(keyword);
                });
        },
        stationListEmptyText() {
            if (!this.stationList.length) {
                return this.$t("m.nearbyStationNoStations");
            }
            return this.stationListMode === "return"
                ? this.$t("m.nearbyStationNoReturnStations")
                : this.$t("m.nearbyStationNoRentStations");
        },
    },
    watch: {
        brand_logo: {
            immediate: true,
            handler(logoUrl) {
                this.preloadBrandLogo(logoUrl);
            },
        },
    },
    async mounted() {
        this.initMapLanguageOptions();
        this.applyRouteContext();
        this.startMapLoading("initial");

        const pendingStationListMode = this.consumePendingStationListMode();

        if (this.isLoggedIn) {
            await this.loadRentingContext();
        } else {
            this.isBorrowStatusResolved = true;
        }

        try {
            this.currentPosition = await this.resolveCurrentPosition();
            if (pendingStationListMode) {
                await this.openStationList(pendingStationListMode);
            }
            await this.loadGoogleMapsWithTimeout();
            this.initMap();
            await this.loadCabinets();
        } catch (error) {
            if (this.notifyGoogleMapsLoadError(error)) {
                // Toast handled for Google Maps load failures.
            } else if (!this.isStationListVisible && !(error && error.suppressMapStatus)) {
                this.isMapUnavailable = true;
                this.mapStatus = error.message || this.$t("m.mapUnavailable");
            }
        } finally {
            this.stopMapLoading();
            this.hasInitialMapLoadCompleted = true;
            await this.loadMapUserInfo();
        }
    },
    activated() {
        if (!this.hasInitialMapLoadCompleted) return;
        this.handleMapReactivated().catch(() => {});
    },
    deactivated() {
        this.stopMapLoading();
        this.resetTransientMapUiState();
    },
    beforeDestroy() {
        this.stopMapLoading();
        this.cleanupGoogleMaps();
        this.cancelDetailSheetCloseAnimation();
        if (this.cabinetRequestTimer) {
            clearTimeout(this.cabinetRequestTimer);
            this.cabinetRequestTimer = null;
        }
        if (this.currentLocationAnimationTimer) {
            clearTimeout(this.currentLocationAnimationTimer);
            this.currentLocationAnimationTimer = null;
        }
        if (this.currentLocationAnimationFrame) {
            cancelAnimationFrame(this.currentLocationAnimationFrame);
            this.currentLocationAnimationFrame = null;
        }
        if (this.currentLocationLandingFrame) {
            cancelAnimationFrame(this.currentLocationLandingFrame);
            this.currentLocationLandingFrame = null;
        }
        if (this.contextRentalTimer) {
            clearInterval(this.contextRentalTimer);
            this.contextRentalTimer = null;
        }
        this.removeDetailSheetDragListeners();
    },
    methods: {
        consumePendingStationListMode() {
            const mode = sessionStorage.getItem(CABINET_MAP_STATION_LIST_MODE_KEY) === "return"
                ? "return"
                : "";

            if (mode) {
                sessionStorage.removeItem(CABINET_MAP_STATION_LIST_MODE_KEY);
            }

            return mode;
        },
        async handleMapReactivated() {
            this.initMapLanguageOptions();

            const pendingStationListMode = this.consumePendingStationListMode();

            if (this.isLoggedIn) {
                await this.loadRentingContext();
                await this.loadMapUserInfo(true);
            } else {
                this.isBorrowStatusResolved = true;
                this.setContextRentDetail({});
            }

            if (pendingStationListMode) {
                await this.openStationList(pendingStationListMode);
            } else if (this.isStationListVisible) {
                await this.loadStationList(true);
            } else {
                this.applyRouteContext();
            }
        },
        preloadBrandLogo(logoUrl) {
            if (!logoUrl) {
                this.isBrandLogoLoaded = false;
                return;
            }

            this.isBrandLogoLoaded = false;

            const image = new Image();
            image.decoding = "async";
            image.onload = () => {
                if (this.brand_logo === logoUrl) {
                    this.isBrandLogoLoaded = true;
                }
            };
            image.onerror = () => {
                if (this.brand_logo === logoUrl) {
                    this.isBrandLogoLoaded = false;
                }
            };
            image.src = logoUrl;

            if (image.complete) {
                this.isBrandLogoLoaded = true;
            }
        },
        handleBrandLogoLoad() {
            this.isBrandLogoLoaded = true;
        },
        handleBrandLogoError() {
            this.isBrandLogoLoaded = false;
        },
        initMapLanguageOptions() {
            let useLangOptions = langOptions;
            const webLangOptions = this.$store.getters["appConfig/web_lang_options"];

            if (webLangOptions && webLangOptions.length > 0) {
                useLangOptions = webLangOptions.reduce((options, key) => {
                    if (langOptions[key]) {
                        options[key] = langOptions[key];
                    }
                    return options;
                }, {});
            }

            if (!useLangOptions[this.$i18n.locale]) {
                const firstLang = Object.keys(useLangOptions)[0];
                if (firstLang) {
                    this.$i18n.locale = firstLang;
                    cks.set("lang", firstLang);
                }
            }

            this.langList = useLangOptions;
        },
        openMapMenu() {
            this.isMapMenuVisible = true;
            this.contextIslandExpanded = false;
        },
        closeMapMenu() {
            this.isMapMenuVisible = false;
        },
        resetTransientMapUiState() {
            this.contextIslandExpanded = false;
            this.closeMapMenu();
            this.isLanguagePopupVisible = false;
            this.isLogoutConfirmVisible = false;
            this.isScannerOpen = false;
            this.isHandlingScanResult = false;
            this.stationSearchKeyword = "";
            this.isStationListVisible = false;
            this.stationListMode = this.isRentingContext ? "return" : "rent";
            this.clearSelectedCabinet();
        },
        startMapLoading(reason = "initial") {
            this.mapLoadingReason = reason;
            if (this.mapLoadingVisibleTimer) {
                clearTimeout(this.mapLoadingVisibleTimer);
                this.mapLoadingVisibleTimer = null;
            }
            if (this.mapLoadingMaxTimer) {
                clearTimeout(this.mapLoadingMaxTimer);
                this.mapLoadingMaxTimer = null;
            }

            this.isMapLoading = true;
            this.isMapLoadingVisible = false;

            this.mapLoadingVisibleTimer = setTimeout(() => {
                if (!this.isMapLoading) return;
                this.isMapLoadingVisible = true;
                this.mapLoadingVisibleTimer = null;
            }, 120);

            this.mapLoadingMaxTimer = setTimeout(() => {
                if (this.mapLoadingVisibleTimer) {
                    clearTimeout(this.mapLoadingVisibleTimer);
                    this.mapLoadingVisibleTimer = null;
                }
                this.isMapLoadingVisible = false;
                this.mapLoadingMaxTimer = null;
            }, GOOGLE_MAPS_LOAD_TIMEOUT);
        },
        stopMapLoading() {
            if (this.mapLoadingVisibleTimer) {
                clearTimeout(this.mapLoadingVisibleTimer);
                this.mapLoadingVisibleTimer = null;
            }
            if (this.mapLoadingMaxTimer) {
                clearTimeout(this.mapLoadingMaxTimer);
                this.mapLoadingMaxTimer = null;
            }
            this.isMapLoading = false;
            this.isMapLoadingVisible = false;
        },
        openMapLanguagePopup() {
            if (!this.showMapLanguageEntry) return;
            this.isMapMenuVisible = false;
            this.isLanguagePopupVisible = true;
        },
        async changeMapLanguage(lang) {
            if (lang === this.$i18n.locale) {
                this.isLanguagePopupVisible = false;
                return;
            }

            this.$i18n.locale = lang;
            cks.set("lang", lang);
            this.isLanguagePopupVisible = false;
            await this.reloadGoogleMapsLanguage();
        },
        openProfilePage() {
            if (!this.isLoggedIn) return;
            this.resetTransientMapUiState();
            this.$router.push({ path: "/profile" }).catch(() => {});
        },
        openHelpPage() {
            this.resetTransientMapUiState();
            this.$router.push({ path: "/help" }).catch(() => {});
        },
        handleMapMenuAuth() {
            if (this.isLoggedIn) {
                this.closeMapMenu();
                this.isLogoutConfirmVisible = true;
                return;
            }
            this.resetTransientMapUiState();
            setPostLoginRedirect({ path: "/cabinet_map" });
            this.$router.push({ path: "/login" });
        },
        confirmLogout() {
            this.$store.dispatch("setToken", "");
            this.$store.dispatch("setIsBorrow", undefined);
            this.$store.dispatch("setOrderInfo", {});
            this.mapUserInfo = {};
            this.isLogoutConfirmVisible = false;
            this.toast(this.$t("m.mapMenuLogoutSuccess"));
        },
        async loadMapUserInfo(force = false) {
            if (!this.isLoggedIn || this.isMapUserInfoLoading) return;
            if (!force && Object.keys(this.mapUserInfo || {}).length) return;

            this.isMapUserInfoLoading = true;
            try {
                const res = await getUserInfo();
                this.mapUserInfo = res && res.data ? res.data : {};
            } catch (_) {
                this.mapUserInfo = {};
            } finally {
                this.isMapUserInfoLoading = false;
            }
        },
        applyRouteContext() {
            this.stationListMode = this.isRentingContext ? "return" : "rent";
        },
        formatMessage(template, values) {
            return Object.keys(values || {}).reduce((text, key) => (
                String(text).replaceAll(`{${key}}`, values[key])
            ), template);
        },
        shortenMiddleText(value, head = 10, tail = 4) {
            const text = String(value || "");
            if (!text) return "";
            if (text.length <= head + tail + 3) return text;
            return `${text.slice(0, head)}...${text.slice(-tail)}`;
        },
        getMachineUnavailableText() {
            const errorCode = Number(this.machine_precheck_error && this.machine_precheck_error.code);
            const translated = this.$t(`eMsg.${errorCode}`);
            if (translated && translated !== `eMsg.${errorCode}`) {
                return translated;
            }
            return (this.machine_precheck_error && this.machine_precheck_error.message) ||
                this.$t("m.currentMachineUnavailableHint");
        },
        handleContextIslandClick() {
            if (!this.contextIslandExpanded) {
                this.contextIslandExpanded = true;
                return;
            }
            if (!this.contextIslandCanNavigate) return;
            this.resetTransientMapUiState();
            this.$router.push({
                path: this.isRentingContext ? "/renting_page" : "/payment_select",
            });
        },
        handleMapBackgroundClick(force = false) {
            if (this.isDetailSheetClosing) return;
            if (!force && !this.isMapUnavailable) return;
            if (Date.now() < this.suppressMapClickUntil) return;
            if (this.contextIslandExpanded) {
                this.contextIslandExpanded = false;
                return;
            }
            this.clearSelectedCabinet();
        },
        cancelDetailSheetCloseAnimation() {
            if (this.detailSheetCloseTimer) {
                clearTimeout(this.detailSheetCloseTimer);
                this.detailSheetCloseTimer = null;
            }
            this.isDetailSheetClosing = false;
        },
        closeSelectedCabinetWithAnimation() {
            this.cancelDetailSheetCloseAnimation();
            this.isDetailSheetClosing = true;
            this.isDetailSheetExpanded = false;
            this.isWorkTimesExpanded = false;
            this.isDetailSheetDragging = false;
            this.detailSheetGestureMode = "";
            this.detailSheetCloseTimer = setTimeout(() => {
                this.detailSheetCloseTimer = null;
                this.clearSelectedCabinet();
            }, 180);
        },
        async loadRentingContext() {
            try {
                const res = await queryBorrowStatus();
                const isBorrow = parseInt(res && res.data && res.data.is_borrow, 10) || 0;
                this.$store.dispatch("setIsBorrow", isBorrow);
                this.$store.dispatch("setOrderInfo", res && res.data && res.data.order ? res.data.order : {});
                if (isBorrow) {
                    this.setContextRentDetail(res.data.order);
                } else {
                    this.setContextRentDetail({});
                }
            } catch (_) {}
            finally {
                this.isBorrowStatusResolved = true;
            }
        },
        setContextRentDetail(order) {
            this.contextRentDetail = order || {};
            this.contextRentalDuration = "--";
            this.startContextRentalTimer();
        },
        startContextRentalTimer() {
            if (this.contextRentalTimer) {
                clearInterval(this.contextRentalTimer);
                this.contextRentalTimer = null;
            }
            if (!this.contextRentDetail.rent_at) return;

            const updateDuration = () => {
                const now = Math.floor(Date.now() / 1000) + 10000;
                const elapsed = Math.max(0, now - this.contextRentDetail.rent_at);
                const hours = Math.floor(elapsed / 3600);
                const minutes = Math.floor((elapsed % 3600) / 60);
                const seconds = elapsed % 60;
                if (hours > 0) {
                    this.contextRentalDuration = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
                    return;
                }
                this.contextRentalDuration = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            };

            updateDuration();
            this.contextRentalTimer = setInterval(updateDuration, 1000);
        },
        resolveCurrentPosition() {
            if (!navigator.geolocation) {
                return Promise.resolve(DEFAULT_CENTER);
            }

            return new Promise((resolve) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            heading: position.coords.heading,
                        });
                    },
                    () => resolve(DEFAULT_CENTER),
                    {
                        enableHighAccuracy: true,
                        timeout: 8000,
                        maximumAge: 60000,
                    }
                );
            });
        },
        showGoogleMapsLoadFailedToast({ retryable = true, message } = {}) {
            const payload = {
                message: message || this.$t("m.googleMapsLoadFailed"),
                persistent: true,
                dismissible: true,
            };
            if (retryable) {
                payload.actionText = this.$t("m.retry");
                payload.action = () => {
                    this.reloadGoogleMapsLanguage();
                };
            }
            this.toast(payload);
        },
        notifyGoogleMapsLoadError(error) {
            if (!(error && error.suppressMapStatus)) {
                return false;
            }

            const message = error.message || this.$t("m.googleMapsLoadFailed");
            this.showGoogleMapsLoadFailedToast({
                retryable: message !== this.$t("m.googleMapsKeyNotConfigured"),
                message,
            });
            return true;
        },
        loadGoogleMaps() {
            if (window.google && window.google.maps) {
                return Promise.resolve();
            }

            const rejectWithToast = (message) => {
                const error = new Error(message);
                error.suppressMapStatus = true;
                return Promise.reject(error);
            };

            const apiKey = GOOGLE_MAPS_KEY;
            if (!apiKey) {
                return rejectWithToast(this.$t("m.googleMapsKeyNotConfigured"));
            }

            const existingScript = document.querySelector("script[data-google-maps]");
            if (existingScript) {
                return new Promise((resolve, reject) => {
                    window[GOOGLE_MAPS_CALLBACK] = resolve;
                    existingScript.addEventListener(
                        "error",
                        () => {
                            const error = new Error(this.$t("m.googleMapsLoadFailed"));
                            error.suppressMapStatus = true;
                            reject(error);
                        },
                        { once: true }
                    );
                });
            }

            return new Promise((resolve, reject) => {
                window[GOOGLE_MAPS_CALLBACK] = resolve;
                const script = document.createElement("script");
                script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${GOOGLE_MAPS_CALLBACK}&language=${encodeURIComponent(this.getGoogleMapsLanguage())}`;
                script.async = true;
                script.defer = true;
                script.dataset.googleMaps = "true";
                script.onerror = () => {
                    const error = new Error(this.$t("m.googleMapsLoadFailed"));
                    error.suppressMapStatus = true;
                    reject(error);
                };
                document.head.appendChild(script);
            });
        },
        loadGoogleMapsWithTimeout(timeoutMs = GOOGLE_MAPS_LOAD_TIMEOUT) {
            let timeoutId = null;
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    const error = new Error(this.$t("m.googleMapsLoadFailed"));
                    error.suppressMapStatus = true;
                    error.timeout = true;
                    reject(error);
                }, timeoutMs);
            });

            return Promise.race([this.loadGoogleMaps(), timeoutPromise]).finally(() => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
            });
        },
        cleanupGoogleMaps() {
            if (this.mapIdleListener) {
                this.mapIdleListener.remove();
                this.mapIdleListener = null;
            }
            if (this.mapClickListener) {
                this.mapClickListener.remove();
                this.mapClickListener = null;
            }
            this.clearCabinetMarkers();
            if (this.userMarker) {
                this.userMarker.setMap(null);
                this.userMarker = null;
            }
            if (this.ownLocationMarker) {
                this.ownLocationMarker.setMap(null);
                this.ownLocationMarker = null;
            }
            this.map = null;
        },
        async reloadGoogleMapsLanguage() {
            if (!this.$refs.map) return this.googleMapsReloadPromise || Promise.resolve();
            if (this.googleMapsReloadPromise) return this.googleMapsReloadPromise;

            this.googleMapsReloadPromise = (async () => {
                this.startMapLoading("initial");
                this.isMapUnavailable = false;
                this.mapStatus = "";
                this.cleanupGoogleMaps();
                const script = document.querySelector("script[data-google-maps]");
                if (script && script.parentNode) {
                    script.parentNode.removeChild(script);
                }
                window.google = undefined;
                window[GOOGLE_MAPS_CALLBACK] = undefined;

                try {
                    await this.loadGoogleMapsWithTimeout();
                    this.initMap();
                    await this.loadCabinets();
                } catch (error) {
                    if (!this.notifyGoogleMapsLoadError(error) && !(error && error.suppressMapStatus)) {
                        this.isMapUnavailable = true;
                        this.mapStatus = error.message || this.$t("m.googleMapsLoadFailed");
                    }
                } finally {
                    this.stopMapLoading();
                    this.googleMapsReloadPromise = null;
                }
            })();

            return this.googleMapsReloadPromise;
        },
        getGoogleMapsLanguage() {
            const locale = (this.$i18n && this.$i18n.locale) || "en";
            const languageMap = {
                zh: "zh-CN",
                en: "en",
                de: "de",
                es: "es",
                fr: "fr",
                it: "it",
                sk: "sk",
            };
            return languageMap[locale] || "en";
        },
        initMap() {
            this.map = new window.google.maps.Map(this.$refs.map, {
                center: this.currentPosition,
                zoom: 13,
                disableDefaultUI: true,
                clickableIcons: false,
                gestureHandling: "greedy",
                backgroundColor: "#e5e8eb",
                styles: [
                    {
                        featureType: "poi.business",
                        stylers: [{ visibility: "off" }],
                    },
                    {
                        featureType: "transit",
                        elementType: "labels.icon",
                        stylers: [{ visibility: "off" }],
                    },
                ],
            });

            this.userMarker = new window.google.maps.Marker({
                position: this.currentPosition,
                map: this.map,
                icon: createCurrentLocationIcon(),
                zIndex: 20,
                clickable: false,
            });
            this.ownLocationMarker = new window.google.maps.Marker({
                position: this.currentPosition,
                map: this.map,
                icon: createOwnLocationIcon(this.currentPosition.heading),
                zIndex: 18,
                clickable: false,
            });
            this.mapClickListener = this.map.addListener("click", () => {
                this.handleMapBackgroundClick(true);
            });
        },
        getPlatform() {
            const userAgent = navigator.userAgent || "";
            if (/android/i.test(userAgent)) return "android";
            if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
            return undefined;
        },
        getMapBoundsParams() {
            if (!this.map) return {};
            const bounds = this.map.getBounds();
            if (!bounds) return {};

            const northEast = bounds.getNorthEast();
            const southWest = bounds.getSouthWest();
            return {
                lat_between: [
                    String(southWest.lat()),
                    String(northEast.lat()),
                ],
                lng_between: [
                    String(southWest.lng()),
                    String(northEast.lng()),
                ],
            };
        },
        getCabinetRequestCenter() {
            if (!this.map) return this.currentPosition;
            const center = this.map.getCenter();
            if (!center) return this.currentPosition;

            return {
                lat: center.lat(),
                lng: center.lng(),
            };
        },
        getCabinetListParams() {
            const platform = this.getPlatform();
            const center = this.getCabinetRequestCenter();
            const params = {
                lat: Number(center.lat),
                lng: Number(center.lng),
                page: 1,
                page_size: 100,
                ...this.getMapBoundsParams(),
            };

            if (platform) {
                params.platform = platform;
            }

            return params;
        },
        getUserLocationCabinetListParams() {
            const platform = this.getPlatform();
            const params = {
                lat: Number(this.currentPosition.lat),
                lng: Number(this.currentPosition.lng),
            };

            if (platform) {
                params.platform = platform;
            }

            return params;
        },
        bindCabinetListRefresh() {
            if (!this.map || this.mapIdleListener) return;
            this.mapIdleListener = this.map.addListener("idle", () => {
                if (this.skipNextMapIdleRefresh) {
                    this.skipNextMapIdleRefresh = false;
                    return;
                }
                if (Date.now() < this.markerInteractionSuppressRefreshUntil) {
                    return;
                }
                this.debounceCabinetListRefresh();
            });
        },
        debounceCabinetListRefresh() {
            if (this.cabinetRequestTimer) {
                clearTimeout(this.cabinetRequestTimer);
            }
            this.clearSelectedCabinet();
            this.liftCurrentLocationPointer(this.getCabinetRequestCenter());
            this.cabinetRequestTimer = setTimeout(() => {
                this.cabinetRequestTimer = null;
                this.loadCabinets({ fitBounds: false });
            }, MAP_CABINET_REQUEST_DEBOUNCE);
        },
        async loadCabinets(options = {}) {
            if (this.cabinetRequestTimer) {
                clearTimeout(this.cabinetRequestTimer);
                this.cabinetRequestTimer = null;
            }
            const requestSeq = ++this.cabinetRequestSeq;
            const requestCenter = this.getCabinetRequestCenter();
            this.liftCurrentLocationPointer(requestCenter);

            try {
                const res = await getNearCabinetList(this.getCabinetListParams());
                if (requestSeq !== this.cabinetRequestSeq) return;

                const list = res.data?.list || [];
                this.cabinetList = list;
                await this.renderCabinetMarkers(list, options);
                this.bindCabinetListRefresh();
            } finally {
                if (requestSeq === this.cabinetRequestSeq) {
                    this.dropCurrentLocationPointer();
                }
            }
        },
        getCabinetPosition(cabinet) {
            const lat = Number(cabinet.lat);
            const lng = Number(cabinet.lng);
            if (
                !Number.isFinite(lat) ||
                !Number.isFinite(lng) ||
                lat < -90 ||
                lat > 90 ||
                lng < -180 ||
                lng > 180
            ) {
                return null;
            }

            return { lat, lng };
        },
        getGroupedCabinets(list) {
            return list.reduce((groups, cabinet) => {
                const position = this.getCabinetPosition(cabinet);
                if (!position) return groups;

                const key = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
                if (!groups[key]) {
                    groups[key] = {
                        position,
                        cabinets: [],
                        primaryCabinet: null,
                        hasOnlineCabinet: false,
                    };
                }

                groups[key].cabinets.push(cabinet);
                const isOnlineCabinet = Number(cabinet.online_status) === 1;
                if (!groups[key].primaryCabinet || (isOnlineCabinet && !groups[key].hasOnlineCabinet)) {
                    groups[key].primaryCabinet = cabinet;
                }
                if (isOnlineCabinet) {
                    groups[key].hasOnlineCabinet = true;
                }

                return groups;
            }, {});
        },
        getCabinetOnlineIcon(cabinet) {
            return (
                cabinet.map_icon_url ||
                cabinet.marker_url ||
                cabinet.icon_url ||
                MAP_ICON_URLS.cabinetOnlineFallback
            );
        },
        hasCustomCabinetIcon(cabinet) {
            return Boolean(cabinet.map_icon_url || cabinet.marker_url || cabinet.icon_url);
        },
        async renderCabinetMarkers(list, options = {}) {
            this.clearCabinetMarkers();
            const bounds = new window.google.maps.LatLngBounds();
            const groupedCabinets = Object.values(this.getGroupedCabinets(list));

            for (const group of groupedCabinets) {
                const primaryCabinet = group.primaryCabinet || group.cabinets[0] || {};
                const count = group.cabinets.length;
                const markerIconUrl = this.getCabinetOnlineIcon(primaryCabinet);
                const markerIcon = await createMarkerIcon(
                    markerIconUrl,
                    count,
                    !group.hasOnlineCabinet,
                    this.hasCustomCabinetIcon(primaryCabinet)
                );

                const marker = new window.google.maps.Marker({
                    position: group.position,
                    map: this.map,
                    title: count > 1
                        ? `${count} stations`
                        : (primaryCabinet.outlets_name || primaryCabinet.address || primaryCabinet.box_number),
                    icon: markerIcon,
                    shape: {
                        type: "rect",
                        coords: markerIcon.shapeCoords,
                    },
                    zIndex: group.hasOnlineCabinet ? 10 : 5,
                });
                marker.addListener("click", () => {
                    this.selectCabinetGroup(group);
                });

                this.cabinetMarkers.push(marker);
                bounds.extend(marker.getPosition());
            }

            if (options.fitBounds !== false && this.cabinetMarkers.length) {
                this.skipNextMapIdleRefresh = true;
                this.map.fitBounds(bounds, {
                    top: 92,
                    right: 38,
                    bottom: 118,
                    left: 38,
                });
            } else if (options.fitBounds !== false) {
                this.skipNextMapIdleRefresh = true;
                this.map.panTo(this.currentPosition);
            }
        },
        clearCabinetMarkers() {
            this.cabinetMarkers.forEach((marker) => marker.setMap(null));
            this.cabinetMarkers = [];
        },
        suppressMapClickClear() {
            this.suppressMapClickUntil = Date.now() + 350;
        },
        prepareCabinetMarkerInteraction() {
            this.suppressMapClickClear();
            this.markerInteractionSuppressRefreshUntil = Date.now() + 900;
            if (this.cabinetRequestTimer) {
                clearTimeout(this.cabinetRequestTimer);
                this.cabinetRequestTimer = null;
            }
            this.cabinetRequestSeq += 1;
        },
        clearSelectedCabinet() {
            this.cancelDetailSheetCloseAnimation();
            this.selectedCabinetGroup = null;
            this.isDetailSheetExpanded = false;
            this.isDetailSheetDragging = false;
            this.detailSheetGestureMode = "";
            this.isWorkTimesExpanded = false;
            this.isStationDetailVisible = false;
        },
        async openStationList(mode = "rent") {
            this.clearSelectedCabinet();
            this.stationListMode = mode;
            this.isStationListVisible = true;
            try {
                await this.loadStationList();
            } catch (error) {
                console.error("[CabinetMap] failed to load station list", error);
            }
        },
        closeStationList() {
            this.isStationListVisible = false;
            this.stationSearchKeyword = "";
            this.clearSelectedCabinet();
            this.stationListMode = "rent";
        },
        async loadStationList(force = false) {
            if (this.stationListLoading && !force) return;

            const requestSeq = ++this.stationListRequestSeq;
            this.stationListLoading = true;
            this.stationList = [];
            try {
                const res = await getNearCabinetList(this.getUserLocationCabinetListParams());
                if (requestSeq !== this.stationListRequestSeq) return;
                this.stationList = res.data?.list || [];
            } finally {
                if (requestSeq === this.stationListRequestSeq) {
                    this.stationListLoading = false;
                }
            }
        },
        selectStationFromList(cabinet) {
            const position = this.getCabinetPosition(cabinet);
            if (!position) return;

            const group = {
                position,
                cabinets: [cabinet],
                primaryCabinet: cabinet,
                hasOnlineCabinet: Number(cabinet.online_status) === 1,
            };

            this.$nextTick(() => {
                if (this.map) {
                    this.skipNextMapIdleRefresh = true;
                    this.map.panTo(position);
                    this.map.setZoom(Math.max(this.map.getZoom(), 15));
                }
                this.selectCabinetGroup(group);
                this.isStationDetailVisible = true;
                this.isDetailSheetExpanded = true;
            });
        },
        backToStationList() {
            this.clearSelectedCabinet();
        },
        getStationCard(cabinet, index) {
            const schedule = this.getCabinetSchedule(cabinet);
            const isOpen = schedule ? schedule.isOpen : Number(cabinet.online_status) === 1;
            const images = this.getCabinetImages(cabinet);
            const available = Number(cabinet.borrow) || 0;
            const empty = Number(cabinet.back) || 0;

            return {
                key: cabinet.id || cabinet.box_id || cabinet.box_number || `${index}-${cabinet.lat}-${cabinet.lng}`,
                cabinet,
                image: images[0] || "",
                title: cabinet.outlets_name || cabinet.box_number || this.$t("m.cabinet"),
                address: cabinet.address1 || cabinet.address || cabinet.address2 || this.$t("m.addressUnavailable"),
                isOpen,
                statusText: isOpen ? this.$t("m.open") : this.$t("m.closed"),
                hoursText: this.getCabinetHoursText(cabinet, schedule, isOpen),
                available,
                empty,
                distance: this.getCabinetDistanceText(cabinet),
            };
        },
        getCabinetDistanceText(cabinet) {
            const distance = this.getCabinetBackendDistance(cabinet);
            const distanceKm = Number(distance);
            if (!Number.isFinite(distanceKm)) return "--";
            return this.formatCabinetDistance(distanceKm * 1000);
        },
        getCabinetBackendDistance(cabinet) {
            const distanceKeys = [
                "distance",
                "distance_meter",
                "distance_meters",
                "distance_m",
                "distanceMeters",
                "distanceInMeters",
            ];
            const key = distanceKeys.find((item) => (
                cabinet &&
                cabinet[item] !== undefined &&
                cabinet[item] !== null &&
                cabinet[item] !== ""
            ));
            return key ? cabinet[key] : NaN;
        },
        getCabinetHoursText(cabinet, schedule, isOpen) {
            if (schedule) {
                return `${schedule.nextAction} ${schedule.nextTime} ${schedule.nextDay}`;
            }

            if (cabinet.start && cabinet.end) {
                return isOpen
                    ? `${this.$t("m.closes")} ${cabinet.end}`
                    : `${this.$t("m.opens")} ${cabinet.start}`;
            }

            return this.$t("m.hoursUnavailable");
        },
        selectCabinetGroup(group) {
            this.prepareCabinetMarkerInteraction();
            this.cancelDetailSheetCloseAnimation();
            this.contextIslandExpanded = false;
            this.selectedCabinetGroup = group;
            console.log("[CabinetMap] selected cabinet", group.primaryCabinet || (group.cabinets && group.cabinets[0]) || null);
            this.isDetailSheetExpanded = false;
            this.isDetailSheetDragging = false;
            this.detailSheetGestureMode = "";
            this.isWorkTimesExpanded = false;
            this.cancelCurrentLocationLandingCircle();
            if (this.currentLocationAnimationFrame) {
                cancelAnimationFrame(this.currentLocationAnimationFrame);
                this.currentLocationAnimationFrame = null;
            }
            if (this.userMarker) {
                this.userMarker.setPosition(group.position);
            }
            this.setCurrentLocationPointerOffset(0);
        },
        getDetailSheetPointerY(event) {
            if (event.touches && event.touches.length) {
                return event.touches[0].clientY;
            }
            if (event.changedTouches && event.changedTouches.length) {
                return event.changedTouches[0].clientY;
            }
            return event.clientY;
        },
        startDetailSheetGesture(event) {
            if (this.isStationDetailVisible) {
                return;
            }
            if (event.target && event.target.closest && event.target.closest("button")) {
                return;
            }

            const pointerY = this.getDetailSheetPointerY(event);
            if (!Number.isFinite(pointerY)) return;

            const contentEl = this.$refs.cabinetDetailContent;
            const startedInContent = Boolean(
                contentEl &&
                event.target &&
                contentEl.contains(event.target)
            );
            let gestureMode = "";

            if (!this.isDetailSheetExpanded) {
                if (startedInContent && contentEl && contentEl.scrollTop > 0) {
                    return;
                }
                gestureMode = "toggle";
            } else if (startedInContent && contentEl.scrollTop <= 0) {
                gestureMode = "collapse";
            }

            if (!gestureMode) return;

            this.isDetailSheetDragging = true;
            this.detailSheetDragStartY = pointerY;
            this.detailSheetGestureMode = gestureMode;
            this.detailSheetContentStartScrollTop = contentEl ? contentEl.scrollTop : 0;
            window.addEventListener("mousemove", this.onDetailSheetDrag);
            window.addEventListener("mouseup", this.endDetailSheetDrag);
            window.addEventListener("touchmove", this.onDetailSheetDrag, { passive: false });
            window.addEventListener("touchend", this.endDetailSheetDrag);
            window.addEventListener("touchcancel", this.endDetailSheetDrag);
        },
        onDetailSheetDrag(event) {
            if (!this.isDetailSheetDragging) return;
            const pointerY = this.getDetailSheetPointerY(event);
            const deltaY = Number.isFinite(pointerY) ? pointerY - this.detailSheetDragStartY : 0;
            const shouldLockScroll = (
                (this.detailSheetGestureMode === "toggle" && Math.abs(deltaY) > 4) ||
                (
                    this.detailSheetGestureMode === "collapse" &&
                    deltaY > 0 &&
                    this.detailSheetContentStartScrollTop <= 0
                )
            );

            if (shouldLockScroll && event.cancelable) {
                event.preventDefault();
            }
        },
        endDetailSheetDrag(event) {
            if (!this.isDetailSheetDragging) return;
            const pointerY = this.getDetailSheetPointerY(event);
            const deltaY = Number.isFinite(pointerY) ? pointerY - this.detailSheetDragStartY : 0;
            const gestureMode = this.detailSheetGestureMode;
            this.removeDetailSheetDragListeners();

            if (gestureMode === "toggle" && deltaY < -32) {
                this.isDetailSheetExpanded = true;
            } else if (gestureMode === "toggle" && deltaY > 42) {
                this.closeSelectedCabinetWithAnimation();
            } else if (gestureMode === "collapse" && deltaY > 42) {
                this.isDetailSheetExpanded = false;
                this.isWorkTimesExpanded = false;
            }
        },
        removeDetailSheetDragListeners() {
            this.isDetailSheetDragging = false;
            this.detailSheetGestureMode = "";
            window.removeEventListener("mousemove", this.onDetailSheetDrag);
            window.removeEventListener("mouseup", this.endDetailSheetDrag);
            window.removeEventListener("touchmove", this.onDetailSheetDrag);
            window.removeEventListener("touchend", this.endDetailSheetDrag);
            window.removeEventListener("touchcancel", this.endDetailSheetDrag);
        },
        formatCabinetDistance(distance) {
            const rawDistance = Number(distance);
            if (!Number.isFinite(rawDistance)) return "--";
            if (rawDistance >= 1000) {
                return `${(rawDistance / 1000).toFixed(1)}km`;
            }
            return `${Math.round(rawDistance).toLocaleString()}m`;
        },
        getDistanceMeters(from, to) {
            if (!from || !to) return NaN;

            const fromLat = Number(from.lat);
            const fromLng = Number(from.lng);
            const toLat = Number(to.lat);
            const toLng = Number(to.lng);
            if (
                !Number.isFinite(fromLat) ||
                !Number.isFinite(fromLng) ||
                !Number.isFinite(toLat) ||
                !Number.isFinite(toLng)
            ) {
                return NaN;
            }

            const toRadians = (degree) => degree * Math.PI / 180;
            const earthRadiusMeters = 6371000;
            const startLat = toRadians(fromLat);
            const endLat = toRadians(toLat);
            const deltaLat = toRadians(toLat - fromLat);
            const deltaLng = toRadians(toLng - fromLng);
            const haversine =
                Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(startLat) * Math.cos(endLat) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
            const centralAngle = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

            return earthRadiusMeters * centralAngle;
        },
        getCabinetImages(cabinet) {
            const imageValue = cabinet && cabinet.img;
            if (Array.isArray(imageValue)) {
                return imageValue.filter(Boolean);
            }
            if (typeof imageValue === "string" && imageValue.trim()) {
                return imageValue.split(",").map((image) => image.trim()).filter(Boolean);
            }
            return [];
        },
        getCabinetWorkTimes(cabinet) {
            const workTimes = Array.isArray(cabinet.work_times) ? cabinet.work_times : [];
            if (!workTimes.length) return [];

            const workTimesByDay = this.getCabinetWorkTimesByDay(workTimes);
            const todayIndex = this.getMondayBasedDayIndex(new Date());
            return WEEK_DAYS.map((_, offset) => (todayIndex + offset) % 7).map((dayIndex) => ({
                day: this.$t(WEEK_DAYS[dayIndex].longKey),
                time: this.formatCabinetWorkTime(workTimesByDay[dayIndex]),
                isToday: dayIndex === todayIndex,
            }));
        },
        getCabinetWorkTimesByDay(workTimes) {
            return workTimes.reduce((days, workTime, index) => {
                const startSeconds = Number(workTime && workTime.s);
                const explicitDayIndex = Number.isFinite(startSeconds)
                    ? Math.floor((((startSeconds % SECONDS_IN_WEEK) + SECONDS_IN_WEEK) % SECONDS_IN_WEEK) / SECONDS_IN_DAY)
                    : index;
                const dayIndex = explicitDayIndex >= 0 && explicitDayIndex < 7 ? explicitDayIndex : index;
                if (dayIndex >= 0 && dayIndex < 7 && !days[dayIndex]) {
                    days[dayIndex] = workTime;
                }
                return days;
            }, Array(7).fill(null));
        },
        getMondayBasedDayIndex(date) {
            return (date.getDay() + 6) % 7;
        },
        formatCabinetWorkTime(workTime) {
            if (!workTime || !Number.isFinite(Number(workTime.s)) || !Number.isFinite(Number(workTime.e))) {
                return this.$t("m.closed");
            }

            const startSeconds = Number(workTime.s);
            const endSeconds = Number(workTime.e);
            if (endSeconds <= startSeconds) {
                return this.$t("m.closed");
            }

            return `${this.formatWorkTimeSeconds(startSeconds)}-${this.formatWorkTimeSeconds(endSeconds)}`;
        },
        getCabinetSchedule(cabinet) {
            const workTimes = Array.isArray(cabinet.work_times) ? cabinet.work_times : [];
            if (!workTimes.length) return null;

            const intervals = workTimes
                .map((workTime) => ({
                    start: Number(workTime && workTime.s),
                    end: Number(workTime && workTime.e),
                }))
                .filter((workTime) => (
                    Number.isFinite(workTime.start) &&
                    Number.isFinite(workTime.end) &&
                    workTime.end > workTime.start
                ))
                .sort((a, b) => a.start - b.start);
            if (!intervals.length) return null;

            const now = new Date();
            const dayIndex = this.getMondayBasedDayIndex(now);
            const nowSeconds = (
                dayIndex * SECONDS_IN_DAY +
                now.getHours() * 60 * 60 +
                now.getMinutes() * 60 +
                now.getSeconds()
            );
            const openInterval = intervals.find((interval) => nowSeconds >= interval.start && nowSeconds < interval.end);
            if (openInterval) {
                return {
                    isOpen: true,
                    nextAction: this.$t("m.closes"),
                    nextTime: this.formatWorkTimeSeconds(openInterval.end),
                    nextDay: this.getWorkTimeShortDay(openInterval.end),
                };
            }

            const upcomingInterval = intervals.find((interval) => interval.start > nowSeconds) || {
                ...intervals[0],
                start: intervals[0].start + SECONDS_IN_WEEK,
            };
            return {
                isOpen: false,
                nextAction: this.$t("m.opens"),
                nextTime: this.formatWorkTimeSeconds(upcomingInterval.start),
                nextDay: this.getWorkTimeShortDay(upcomingInterval.start),
            };
        },
        getWorkTimeShortDay(seconds) {
            const normalizedSeconds = ((Number(seconds) % SECONDS_IN_WEEK) + SECONDS_IN_WEEK) % SECONDS_IN_WEEK;
            const dayIndex = Math.floor(normalizedSeconds / SECONDS_IN_DAY);
            return this.$t((WEEK_DAYS[dayIndex] || WEEK_DAYS[0]).shortKey);
        },
        formatWorkTimeSeconds(seconds) {
            const normalizedSeconds = ((Number(seconds) % SECONDS_IN_DAY) + SECONDS_IN_DAY) % SECONDS_IN_DAY;
            const totalMinutes = Math.floor(normalizedSeconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours === 12 && minutes === 0) {
                return this.$t("m.noon");
            }

            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        },
        toggleWorkTimes() {
            this.isWorkTimesExpanded = !this.isWorkTimesExpanded;
        },
        openDirections() {
            const cabinet = this.selectedCabinet || {};
            const position = this.getCabinetPosition(cabinet);
            if (!position) return;
            const origin = `${this.currentPosition.lat},${this.currentPosition.lng}`;
            const destination = `${position.lat},${position.lng}`;
            window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&hl=${encodeURIComponent(this.getGoogleMapsLanguage())}`, "_blank");
        },
        callSelectedCabinet() {
            const phone = (this.selectedCabinet || {}).phone;
            if (!phone) return;
            window.location.href = `tel:${phone}`;
        },
        goBack() {
            if (this.isRentingContext) {
                this.$router.push({ path: "/renting_page" });
                return;
            }
            if (this.isMachineAvailableContext) {
                this.$router.push({ path: "/payment_select" });
                return;
            }
            if (this.isMachineUnavailableContext) {
                const error = this.machine_precheck_error || {};
                this.$router.push({
                    path: "/machine_config_error",
                    query: {
                        code: this.contextScannedCode,
                        errorCode: error.code,
                        message: error.message,
                    },
                });
                return;
            }
            if (window.history.length > 1) {
                this.$router.go(-1);
                return;
            }
            this.$router.push({ path: "/payment_select" });
        },
        openScanner() {
            if (this.isRentingContext) return;
            if (this.isScannerOpen) return;
            this.isScannerOpen = true;
            this.isHandlingScanResult = false;
        },
        closeScanner() {
            this.isScannerOpen = false;
            this.isHandlingScanResult = false;
        },
        parseDeviceCodeFromScanResult(text) {
            const result = String(text || "").trim();
            if (!result) return "";

            try {
                const url = new URL(result);
                const pathParts = url.pathname.split("/").filter(Boolean);
                return pathParts.length ? pathParts[pathParts.length - 1] : "";
            } catch (_) {
                return "";
            }
        },
        async handleScanResult(text) {
            if (this.isHandlingScanResult || !text) return;
            const deviceCode = String(text).trim();

            this.isHandlingScanResult = true;
            this.isScannerOpen = false;
            this.$store.commit("appConfig/SET_DEVICE_CODE", deviceCode);
            sessionStorage.setItem("lastDeviceCode", deviceCode);
            sessionStorage.removeItem("hasFetchedAppConfig");

            let routeAfterScan = { path: "/payment_select" };
            try {
                const result = await this.$store.dispatch("appConfig/fetchAppConfig");
                const precheckError = result && result.machinePrecheckError;
                if (precheckError) {
                    routeAfterScan = {
                        path: "/machine_config_error",
                        query: {
                            code: precheckError.device_code,
                            errorCode: precheckError.code,
                            message: precheckError.message,
                        },
                    };
                } else if (!this.$store.getters.token) {
                    setPostLoginRedirect(routeAfterScan);
                }
            } finally {
                this.$router.push(routeAfterScan);
            }
        },
        focusCurrentLocation() {
            if (!this.map) return;
            this.map.panTo(this.currentPosition);
            this.map.setZoom(Math.max(this.map.getZoom(), 14));
            this.liftCurrentLocationPointer(this.currentPosition);
            this.debounceCabinetListRefresh();
        },
        cancelCurrentLocationLandingCircle() {
            if (this.currentLocationLandingFrame) {
                cancelAnimationFrame(this.currentLocationLandingFrame);
                this.currentLocationLandingFrame = null;
            }
        },
        setCurrentLocationPointerOffset(offset, landingCircleProgress = 0) {
            this.currentLocationPointerOffset = offset;
            if (this.userMarker) {
                this.userMarker.setIcon(createCurrentLocationIcon(offset, landingCircleProgress));
            }
        },
        animateCurrentLocationPointerOffset(targetOffset, duration = 140, onComplete) {
            if (!this.userMarker) return;

            if (this.currentLocationAnimationTimer) {
                clearTimeout(this.currentLocationAnimationTimer);
                this.currentLocationAnimationTimer = null;
            }
            if (this.currentLocationAnimationFrame) {
                cancelAnimationFrame(this.currentLocationAnimationFrame);
                this.currentLocationAnimationFrame = null;
            }

            const startOffset = this.currentLocationPointerOffset;
            const startTime = performance.now();
            const animate = (time) => {
                if (!this.userMarker) return;
                const progress = Math.min((time - startTime) / duration, 1);
                const easedProgress = 1 - Math.pow(1 - progress, 3);
                const offset = startOffset + ((targetOffset - startOffset) * easedProgress);
                this.setCurrentLocationPointerOffset(offset);

                if (progress < 1) {
                    this.currentLocationAnimationFrame = requestAnimationFrame(animate);
                    return;
                }

                this.setCurrentLocationPointerOffset(targetOffset);
                this.currentLocationAnimationFrame = null;
                if (onComplete) {
                    onComplete();
                }
            };

            this.currentLocationAnimationFrame = requestAnimationFrame(animate);
        },
        liftCurrentLocationPointer(position) {
            if (!this.userMarker) return;
            this.cancelCurrentLocationLandingCircle();
            if (position) {
                this.userMarker.setPosition(position);
            }
            this.animateCurrentLocationPointerOffset(8, 120);
        },
        dropCurrentLocationPointer() {
            this.animateCurrentLocationPointerOffset(0, 120, () => {
                this.playCurrentLocationLandingCircle();
            });
        },
        playCurrentLocationLandingCircle() {
            if (!this.userMarker) return;
            this.cancelCurrentLocationLandingCircle();

            const duration = 260;
            const startTime = performance.now();
            const animate = (time) => {
                if (!this.userMarker) return;
                const progress = Math.min((time - startTime) / duration, 1);
                this.setCurrentLocationPointerOffset(0, progress);

                if (progress < 1) {
                    this.currentLocationLandingFrame = requestAnimationFrame(animate);
                    return;
                }

                this.setCurrentLocationPointerOffset(0);
                this.currentLocationLandingFrame = null;
            };

            this.currentLocationLandingFrame = requestAnimationFrame(animate);
        },
    },
};
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.cabinet-map-page {
    position: absolute;
    inset: 0;
    min-height: 100%;
    background: #e5e8eb;
    overflow: hidden;
}

.cabinet-map {
    position: absolute;
    inset: 0;
}

.map-loading-overlay {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 12;
    pointer-events: none;
}

.map-loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    min-width: 160px;
    max-width: calc(100vw - 48px);
    padding: 15px 18px 14px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 8px 24px rgba(14, 31, 53, 0.12);
    color: #202124;
    text-align: center;
    font-family: $font-family;
    pointer-events: none;
}

.map-loading-spinner {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid var(--theme-selectable-accent-bg, rgba(39, 108, 225, 0.16));
    border-top-color: var(--theme-selectable-accent-color, #276CE1);
    animation: loading 0.9s linear infinite;
}

.map-loading-text {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.1px;
}

.map-loading-fade-enter-active,
.map-loading-fade-leave-active {
    transition: opacity 160ms ease;
}

.map-loading-fade-enter,
.map-loading-fade-leave-to {
    opacity: 0;
}

.map-status {
    position: absolute;
    top: 50%;
    left: 24px;
    right: 24px;
    transform: translateY(-50%);
    padding: 14px 16px;
    border-radius: $border-radius;
    background: rgba(255, 255, 255, 0.92);
    color: $text-color;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
}

.map-icon-img {
    width: 16px;
    height: 16px;
}

.map-location-btn .map-icon-img {
    width: 17.5px;
    height: 17.5px;
}

.map-btn {
    position: absolute;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 7.8px 0 rgba(0, 0, 0, 0.10);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.map-side-menu-mask {
    position: absolute;
    inset: 0;
    z-index: 80;
    background: rgba(0, 0, 0, 0.2);
}

.map-side-menu {
    --map-side-menu-brand-top: calc(84px + env(safe-area-inset-top));
    --map-side-menu-brand-max-height: 90px;
    --map-side-menu-user-gap: 14px;
    --map-side-menu-list-top: calc(var(--map-side-menu-brand-top) + var(--map-side-menu-brand-max-height) + 72px);
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 81;
    width: min(268px, 72vw);
    background: #fff;
    border-radius: 0 6px 6px 0;
    box-shadow: -2px 3px 7.9px 0 rgba(36, 54, 84, 0.3);
    color: #333;
    font-family: $font-family;
    overflow: hidden;
}

.map-side-menu-brand {
    position: absolute;
    left: 33px;
    right: 24px;
    top: var(--map-side-menu-brand-top);
    min-height: 32px;
    display: flex;
    align-items: center;
    gap: 7px;
    color: #31345e;
    font-size: 23px;
    font-weight: 600;
    line-height: 30px;
}

.map-side-menu-brand .brand-logo {
    display: none;
    width: auto;
    height: auto;
    max-width: 80%;
    max-height: var(--map-side-menu-brand-max-height);
    object-fit: contain;
    object-position: left center;
}

.map-side-menu-brand .brand-logo.loaded {
    display: block;
}

.map-side-menu-brand .brand-icon {
    width: 29px;
    height: 29px;
    border-radius: 6px;
    object-fit: contain;
    flex: 0 0 29px;
}

.map-side-menu-user {
    position: absolute;
    left: 34px;
    right: 24px;
    top: calc(var(--map-side-menu-brand-top) + var(--map-side-menu-brand-max-height) + var(--map-side-menu-user-gap));
    color: #9b9b9b;
    font-family: $font-family;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.map-side-menu-list {
    position: absolute;
    left: 0;
    right: 0;
    top: var(--map-side-menu-list-top);
}

.map-side-menu-item {
    width: 100%;
    height: 52px;
    border: 0;
    background: transparent;
    color: #333;
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 0 33px;
    font-family: $font-family;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.32px;
    text-align: left;
}

.map-side-menu-icon {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    object-fit: contain;
}

.map-menu-fade-enter-active,
.map-menu-fade-leave-active {
    transition: opacity 160ms ease;
}

.map-menu-fade-enter,
.map-menu-fade-leave-to {
    opacity: 0;
}

.map-side-menu-slide-enter-active,
.map-side-menu-slide-leave-active {
    transition: transform 180ms ease;
}

.map-side-menu-slide-enter,
.map-side-menu-slide-leave-to {
    transform: translateX(-100%);
}

.map-language-options {
    padding: 0 23px;
}

.map-language-option {
    display: block;
    width: 100%;
    min-height: 50px;
    border: 0;
    border-bottom: 1px solid #ddd;
    background: #fff;
    color: #333;
    font-family: $font-family;
    font-size: 16px;
    line-height: 20px;
    text-align: left;
    padding: 15px 25px;
    position: relative;
}

.map-language-option:last-child {
    border-bottom: 0;
}

.map-language-option.selected {
    font-weight: 700;
}

.map-language-option.selected::after {
    content: "";
    position: absolute;
    right: 24px;
    top: 18px;
    width: 10px;
    height: 6px;
    border-left: 2px solid #276ce1;
    border-bottom: 2px solid #276ce1;
    transform: rotate(-45deg);
}

.map-logout-confirm {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.map-logout-content {
    color: #666666;
    font-size: 16px;
    line-height: 23.04px;
}

.map-logout-confirm-btn {
    width: 100%;
    margin-top: 20px;
}

.map-menu-btn {
    left: 16px;
    top: calc(15px + env(safe-area-inset-top));
}

.map-search-btn {
    right: 16px;
    top: calc(15px + env(safe-area-inset-top));
}

.map-context-island {
    position: absolute;
    left: 62px;
    right: 62px;
    top: calc(15px + env(safe-area-inset-top));
    z-index: 5;
    height: 36px;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 1px 7.8px 0 rgba(0, 0, 0, 0.10);
    color: #333;
    font-family: $font-family;
    padding: 0 24px;
    box-sizing: border-box;
    overflow: hidden;
    transition: left 160ms ease, right 160ms ease, height 160ms ease, border-radius 160ms ease, padding 160ms ease;
}

.map-context-island.expanded {
    left: 16px;
    right: 16px;
    height: 100px;
    border-radius: 8px;
    padding: 10px 34px 10px 14px;
}

.map-context-island.actionable {
    cursor: pointer;
}

.map-context-main {
    position: absolute;
    left: 24px;
    right: 24px;
    top: 11px;
    min-width: 0;
    width: auto;
    box-sizing: border-box;
    transition: left 160ms ease, right 160ms ease;
}

.map-context-island.expanded .map-context-main {
    left: 14px;
    right: 34px;
}

.map-context-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.map-context-title {
    color: #202124;
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
}

.map-context-collapsed-dot {
    position: absolute;
    top: 3px;
    left: -13px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #35beaf;
    flex-shrink: 0;
}

.map-context-collapsed-dot.unavailable {
    background: #ff5e5e;
}

.map-context-collapsed-dot.renting {
    background: #276ce1;
}

.map-context-detail {
    position: absolute;
    left: 14px;
    right: 34px;
    top: 36px;
    padding-top: 7px;
    border-top: 1px solid #edf0f3;
    color: #555;
    font-size: 12px;
    line-height: 16px;
}

.map-context-detail.renting {
    padding-top: 8px;
}

.map-context-rental-metric {
    display: flex;
    align-items: baseline;
    min-width: 0;
    gap: 3px;
    white-space: nowrap;
    overflow: hidden;
}

.map-context-rental-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 5px;
    min-width: 0;
}

.map-context-rental-row .map-context-rental-metric {
    flex: 0 1 auto;
}

.map-context-rental-row .map-context-rental-metric:last-child {
    margin-left: auto;
    justify-content: flex-end;
    text-align: right;
}

.map-context-rental-label {
    color: #9b9b9b;
    font-size: 12px;
    line-height: 16px;
    flex: 0 0 auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

.map-context-rental-value {
    color: #276ce1;
    font-family: Poppins, $font-family;
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -1px;
    margin-left: 0;
    flex: 0 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

.map-context-rental-action {
    margin-top: 8px;
    color: #9b9b9b;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.map-context-status,
.map-context-action {
    display: flex;
    align-items: center;
    min-width: 0;
}

.map-context-status > span:last-child {
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}

.map-context-action {
    display: block;
    margin-top: 4px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.map-context-status-dot {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    margin-right: 7px;
    border-radius: 50%;
    background: #35beaf;
}

.map-context-status-dot.unavailable {
    background: #ff5e5e;
}

.map-context-status-dot.renting {
    background: #276ce1;
}

.map-context-toggle-arrow {
    position: absolute;
    right: 11px;
    top: 12px;
    width: 8px;
    height: 8px;
    border-right: 1.6px solid #9b9b9b;
    border-bottom: 1.6px solid #9b9b9b;
    transform: rotate(45deg);
}

.map-context-toggle-arrow.expanded {
    display: none;
}

.map-context-arrow {
    position: absolute;
    right: 14px;
    top: 18px;
    width: 8px;
    height: 8px;
    border-right: 1.6px solid #9b9b9b;
    border-bottom: 1.6px solid #9b9b9b;
    transform: translateY(-50%) rotate(-45deg);
}

.scan-btn {
    position: absolute;
    left: 50%;
    bottom: calc(39px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    width: 223px;
    height: 46px;
    border: 0;
    border-radius: $border-radius;
    background: var(--theme-primary-btn-bg);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: $font-family;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
}

.scan-btn.touch-active {
    transform: translateX(-50%) scale(0.985);
}

.map-location-btn {
    right: 16px;
    bottom: calc(44px + env(safe-area-inset-bottom));
}

.scan-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
}

.station-list-page {
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f7f7f7;
    color: #333;
    font-family: $font-family;
}

.station-list-header {
    position: relative;
    flex: 0 0 calc(150px + env(safe-area-inset-top));
    background: #fff;
}

.station-list-header::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 72px;
    pointer-events: none;
}

.station-list-back,
.cabinet-detail-back {
    position: absolute;
    left: 8px;
    top: calc(5px + env(safe-area-inset-top));
    z-index: 2;
    width: 24px;
    height: 34px;
    border: 0;
    background: transparent;
    padding: 0;
}

.station-list-back::before,
.cabinet-detail-back::before {
    content: "";
    position: absolute;
    left: 9px;
    top: 11px;
    width: 9px;
    height: 9px;
    border-left: 1.6px solid #333;
    border-bottom: 1.6px solid #333;
    transform: rotate(45deg);
}

.station-list-header h1 {
    position: absolute;
    top: calc(11px + env(safe-area-inset-top));
    left: 50%;
    z-index: 2;
    width: 180px;
    margin: 0;
    transform: translateX(-50%);
    color: #333;
    font-family: $font-family;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
}

.station-search-field {
    position: absolute;
    left: 16px;
    right: 16px;
    top: calc(60px + env(safe-area-inset-top));
    z-index: 2;
    height: 40px;
    border-radius: 33px;
    background: #f7f7f7;
    display: flex;
    align-items: center;
    padding: 0 16px 0 12px;
}

.station-search-icon {
    width: 17px;
    height: 17px;
    flex: 0 0 17px;
    margin-right: 7px;
    background: #276ce1;
    mask: url("~@/assets/map/search.svg") center / contain no-repeat;
    -webkit-mask: url("~@/assets/map/search.svg") center / contain no-repeat;
}

.station-search-field input {
    min-width: 0;
    flex: 1 1 auto;
    border: 0;
    outline: 0;
    background: transparent;
    color: #333;
    font-family: $font-family;
    font-size: 12px;
    line-height: 15px;
    padding: 0;
}

.station-search-field input::placeholder {
    color: #9d9d9d;
    opacity: 1;
}

.station-search-field input::-webkit-search-cancel-button {
    display: none;
}

.station-tabs {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    z-index: 2;
    height: 30px;
    display: grid;
    grid-template-columns: 1fr 1fr;
}

.station-tabs button {
    position: relative;
    border: 0;
    background: transparent;
    color: #555;
    font-family: $font-family;
    font-size: 15px;
    font-weight: 400;
    line-height: 18px;
    padding: 0 0 11px;
}

.station-tabs button.active {
    color: #276ce1;
    font-weight: 500;
}

.station-tabs button.active::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: #276ce1;
}

.station-list-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 10px 16px calc(24px + env(safe-area-inset-bottom));
    -webkit-overflow-scrolling: touch;
}

.station-card {
    position: relative;
    display: block;
    width: 100%;
    height: 121px;
    margin: 0 0 10px;
    border: 0;
    border-radius: 5px;
    background: #fff;
    box-shadow: $shadow;
    color: #333;
    padding: 0;
    text-align: left;
    overflow: hidden;
    font-family: $font-family;
}

.station-card-skeleton {
    pointer-events: none;
}

.station-card-skeleton-image,
.station-card-skeleton-line,
.station-card-skeleton-metrics i {
    display: block;
    border-radius: 5px;
    background: linear-gradient(90deg, #eeeeee 0%, #f7f7f7 42%, #eeeeee 84%);
    background-size: 220% 100%;
    animation: stationSkeletonPulse 1.15s ease-in-out infinite;
}

.station-card-skeleton-image {
    position: absolute;
    left: 15px;
    top: 15px;
    width: 58px;
    height: 58px;
}

.station-card-skeleton-main {
    position: absolute;
    left: 83px;
    right: 18px;
    top: 17px;
}

.station-card-skeleton-line {
    height: 12px;
    margin-bottom: 10px;
}

.station-card-skeleton-line.title {
    width: 62%;
    height: 15px;
}

.station-card-skeleton-line.address {
    width: 92%;
}

.station-card-skeleton-line.hours {
    width: 52%;
}

.station-card-skeleton-metrics {
    position: absolute;
    left: 15px;
    right: 15px;
    bottom: 16px;
    display: grid;
    grid-template-columns: 92px 92px 1fr;
    gap: 18px;
}

.station-card-skeleton-metrics i {
    height: 12px;
}

@keyframes stationSkeletonPulse {
    0% {
        background-position: 120% 0;
    }
    100% {
        background-position: -120% 0;
    }
}

.station-card-image-wrap {
    position: absolute;
    left: 15px;
    top: 15px;
    width: 58px;
    height: 58px;
    border-radius: 5px;
    overflow: hidden;
    background: #d9d9d9;
}

.station-card-image-wrap img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.station-card-main {
    position: absolute;
    left: 83px;
    right: 18px;
    top: 14px;
    display: block;
}

.station-card-main strong,
.station-card-address,
.station-card-hours {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.station-card-main strong {
    color: #333;
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
}

.station-card-address {
    margin-top: 6px;
    color: #555;
    font-size: 11px;
    font-weight: 400;
    line-height: 13px;
}

.station-card-hours {
    margin-top: 6px;
    color: #555;
    font-size: 11px;
    font-weight: 400;
    line-height: 13px;
}

.station-card-hours .open {
    color: #276ce1;
}

.station-card-hours .closed {
    color: #ff5e5e;
}

.station-card-dot {
    margin: 0 7px;
    color: #555;
}

.station-card-divider {
    position: absolute;
    left: 15px;
    right: 15px;
    top: 82px;
    height: 1px;
    background-image: linear-gradient(to right, #e6e6e6 55%, transparent 55%);
    background-size: 13px 1px;
    background-repeat: repeat-x;
    background-position: bottom;
}

.station-card-metrics {
    position: absolute;
    left: 15px;
    right: 15px;
    bottom: 13px;
    height: 17px;
    display: grid;
    grid-template-columns: 123px 1fr 66px;
    align-items: end;
}

.station-card-metrics span {
    display: flex;
    align-items: flex-end;
    min-width: 0;
}

.station-card-metrics small {
    color: #9b9b9b;
    font-size: 11px;
    font-weight: 600;
    line-height: 13px;
    text-transform: uppercase;
}

.station-card-metrics b {
    margin-left: 13px;
    color: #276ce1;
    font-size: 16px;
    font-weight: 600;
    line-height: 17px;
}

.station-card-metrics span:nth-child(2) b {
    color: #35beaf;
}

.station-card-metrics b.muted {
    color: #d9d9d9;
}

.station-card-distance {
    justify-content: flex-end;
    gap: 10px;
}

.station-card-distance small {
    color: #555;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    text-transform: none;
}

.station-card-distance svg {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    display: block;
}

.station-list-empty {
    margin-top: 24px;
    color: #9b9b9b;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
}

.cabinet-detail-overlay {
    position: absolute;
    inset: 0;
    z-index: 34;
    background-color: rgba(0, 0, 0, 0.5);
    pointer-events: auto;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.cabinet-detail-sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 35;
    height: min(394px, 52vh);
    min-height: 340px;
    border-radius: 16px 16px 0 0;
    background: #fff;
    color: #2a2d33;
    box-shadow: 0 -5px 18px rgba(0, 0, 0, 0.16);
    display: flex;
    flex-direction: column;
    transition: height 180ms ease-out, transform 180ms ease-out, opacity 180ms ease-out;
    overflow: hidden;
    touch-action: pan-y;
    transform: translateY(0);
    will-change: transform, height, opacity;
}

.cabinet-detail-sheet.station-detail-fullscreen {
    top: 0;
    height: auto;
    min-height: 0;
    z-index: 45;
    border-radius: 0;
    box-shadow: none;
    transition: none;
    touch-action: auto;
}

.cabinet-detail-sheet.closing {
    transform: translateY(calc(100% + 16px));
    opacity: 0;
    pointer-events: none;
}

.station-detail-fullscreen .cabinet-detail-header {
    padding: calc(60px + env(safe-area-inset-top)) 16px 14px;
}

.station-detail-fullscreen .cabinet-detail-handle {
    display: none;
}

.cabinet-detail-sheet.expanded {
    height: calc(100% - 28px - env(safe-area-inset-top));
    border-radius: 0;
}

.cabinet-detail-sheet.station-detail-fullscreen {
    height: auto;
}

.cabinet-detail-sheet.dragging {
    transition: none;
}

.cabinet-detail-header {
    position: relative;
    padding: 10px 16px 14px;
    background: #fff;
    flex: 0 0 auto;
    user-select: none;
    z-index: 2;
}

.cabinet-detail-handle {
    width: 47px;
    height: 4px;
    margin: 0 auto 9px;
    border-radius: 999px;
    background: #c7c7c7;
}

.cabinet-detail-collapse {
    position: absolute;
    left: 8px;
    top: 9px;
    width: 36px;
    height: 34px;
    border: 0;
    background: transparent;
    padding: 0;
}

.cabinet-detail-collapse::before,
.cabinet-detail-collapse::after {
    content: "";
    position: absolute;
    top: 16px;
    width: 12px;
    height: 3px;
    border-radius: 3px;
    background: #2a2d33;
}

.cabinet-detail-collapse::before {
    left: 9px;
    transform: rotate(45deg);
}

.cabinet-detail-collapse::after {
    left: 16px;
    transform: rotate(-45deg);
}

.cabinet-detail-content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 16px 14px;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.cabinet-detail-title {
    margin: 0 0 9px;
    color: #202124;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
}

.cabinet-detail-address {
    color: #555;
    font-size: 12px;
    line-height: 1.3;
}

.cabinet-detail-status {
    margin-top: 9px;
    color: #555;
    font-size: 14px;
    line-height: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.cabinet-detail-status .closed,
.cabinet-detail-row .closed {
    color: #ff5e5e;
}

.cabinet-detail-status .open {
    color: #35beaf;
}

.cabinet-detail-dot {
    margin: 0;
    color: #555;
    font-size: 12px;
}

.cabinet-detail-metrics {
    display: grid;
    grid-template-columns: 76px 69px 1fr;
    padding-top: 10px;
    border-top: 1px solid #edf0f3;
    border-bottom: 1px solid #edf0f3;
    padding-bottom: 10px;
}

.cabinet-detail-content.no-gallery .cabinet-detail-metrics {
    border-bottom: 0;
}

.cabinet-detail-metrics span {
    display: block;
    color: #9b9b9b;
    font-size: 11px;
    font-weight: 600;
    line-height: 13px;
    letter-spacing: 0;
}

.cabinet-detail-metrics strong {
    display: block;
    margin-top: 3px;
    color: #2a2d33;
    font-size: 18px;
    font-weight: 600;
    line-height: 21px;
}

.cabinet-detail-metrics div {
    min-width: 0;
}

.cabinet-detail-metrics div + div {
    border-left: 1px solid #edf0f3;
    padding-left: 14px;
}

.cabinet-detail-metrics div:first-child strong {
    color: #276ce1;
}

.cabinet-detail-metrics div:nth-child(2) strong {
    color: #35beaf;
}

.cabinet-detail-metrics div strong.empty {
    color: #d9d9d9;
}

.cabinet-detail-gallery {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    margin-bottom: 14px;
    overflow-x: auto;
    padding-bottom: 2px;
}

.cabinet-detail-gallery img {
    width: 160px;
    height: 201px;
    flex: 0 0 160px;
    border-radius: 8px;
    object-fit: cover;
}

.cabinet-detail-row {
    display: flex;
    align-items: center;
    min-height: 48px;
    border-top: 1px solid #edf0f3;
    color: #555;
    font-size: 14px;
    line-height: 15px;
}

.cabinet-detail-row-icon {
    width: 18px;
    height: 18px;
    margin-right: 15px;
    flex: 0 0 18px;
    object-fit: contain;
    color: #276ce1;
}

.cabinet-detail-worktime {
    border-top: 1px solid #edf0f3;
}

.cabinet-detail-worktime-toggle {
    width: 100%;
    border: 0;
    border-top: 0;
    background: transparent;
    padding: 0;
    text-align: left;
}

.cabinet-detail-row-chevron {
    width: 8px;
    height: 8px;
    margin-left: auto;
    border-right: 1.5px solid #9b9b9b;
    border-bottom: 1.5px solid #9b9b9b;
    transform: rotate(45deg);
    transition: transform 160ms ease;
}

.cabinet-detail-worktime.open .cabinet-detail-row-chevron {
    transform: rotate(225deg);
}

.cabinet-detail-worktime-list {
    padding: 0 0 12px 33px;
}

.cabinet-detail-worktime-item {
    display: grid;
    grid-template-columns: 124px 1fr;
    min-height: 32px;
    align-items: center;
    color: #555;
    font-size: 14px;
    line-height: 17px;
}

.cabinet-detail-worktime-item strong {
    color: #555;
    font-weight: 400;
}

.cabinet-detail-worktime-item.today,
.cabinet-detail-worktime-item.today strong {
    color: #276ce1;
    font-weight: 600;
}

.cabinet-detail-actions {
    flex: 0 0 46px;
    position: relative;
    z-index: 3;
    display: flex;
    gap: 10px;
    height: 46px;
    margin: 0 16px calc(39px + env(safe-area-inset-bottom));


}

.cabinet-detail-actions::before {
    content: "";
    position: absolute;
    left: -16px;
    right: -16px;
    top: -10px;
    bottom: calc(-50px - env(safe-area-inset-bottom));
    z-index: -1;
    background: #fff;
}

.cabinet-detail-actions button {
    min-width: 0;
    height: 46px;
    border: 0;
    border-radius: 5px;
    padding: 0 14px;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
}

.cabinet-action-primary {
    flex: 1 1 0;
    background: #276ce1;
    color: #fff;
}

.cabinet-action-secondary {
    flex: 1 1 0;
    background: #f2f3f5;
    color: #276ce1;
}

.cabinet-action-secondary:disabled {
    color: #a5abb4;
}

.cabinet-action-secondary:disabled .cabinet-action-icon {
    opacity: 0.45;
}

.cabinet-action-icon {
    width: 20px;
    height: 20px;
    flex: 0 0 18px;
    object-fit: contain;
}

.cabinet-action-secondary .cabinet-action-icon {
    width: 19px;
    height: 19px;
    flex-basis: 16px;
}

</style>
